import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  Dialog,
  Disclose,
  Loading,
  Refusal,
  Tooltip,
  controlGet,
  controlPost,
} from "@lopie/kernel-ui";

// The same function the Library's install form uses to show the hostname as
// somebody types, imported rather than reimplemented. Two mirrors of one
// server-side rule is two chances to show an address the control plane will
// not accept, and the mismatch would only appear at the moment of approval.
import { slugOf } from "./deploy.jsx";
import { controlFailure, sandboxState, wantsSandbox, withoutSandboxes } from "./sandbox.js";
import { wantsEvidence } from "./evidenceReview.js";
import { DecisionEvidence } from "./evidenceView.jsx";
import { liveLink, narrowing, NO_NARROWING } from "./sandboxReview.js";
import {
  CallLog, ObservedEgress, QaReport, UnusedBlock, UnusedMark, useSandboxEvidence,
} from "./sandboxView.jsx";
// What the approve dialog does about the sandbox gate (LO-191): the words that
// follow the kernel's mode, the four refusals, and the window that waits for a
// narrowed decision's re-run. The rules are in approval.js and are tested there.
import {
  describeAggregate, followThroughLeft, followThroughSays, gateRefusal, gateWords, installFor,
  isWaiting, openFollowThrough, orphanedAggregates, startedFollowThrough, SYNCMAP_CHARTABLE, whoIs, windowFollowsOn,
} from "./approval.js";
import { GateRefusal, RerunPanel } from "./approvalView.jsx";
import OpenSandboxes from "./OpenSandboxes.jsx";
// What a second version changes, against the manifest in force and the live build
// (LO-203). The arithmetic is in versionDiff.js and is tested there.
import { ceilingDiff, liveBuildOf, sourceDiff } from "./versionDiff.js";

/**
 * `app-vendor-spend` -> `Vendor Spend`, as an opening suggestion only.
 *
 * The `app-` prefix is dropped because it is a namespace marker rather than
 * part of the app's name — it exists so the provisioner can be granted `app-*`
 * once instead of one prefix per app, and repeating it in a title would put
 * plumbing in front of a person.
 */
function titleOf(appType) {
  return String(appType || "")
    .replace(/^app-/, "")
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Pending apps, pending types, pending mappings: three proposals and one
 * decision, decided by one governor list (LO-61, extended for LO-126's
 * reversal).
 *
 * Until this screen there was no way to *build* a manifest, and until this
 * change there was no way to propose a kind of record or a table-to-type
 * mapping either — both stopped at whoever held commit access, which is the
 * exact bottleneck LO-61 solved for manifests. Object-type and sync-map
 * requests are the same shape one domain over: a proposal that grants
 * nothing by existing, reviewed here, and either written or refused with a
 * reason.
 *
 * The three kinds are merged into one queue rather than three screens or
 * three tabs, because the person deciding them is the same person reading
 * the same governor list — a queue split three ways would be three places to
 * forget to check. Each card says which kind it is, in words, because a type
 * proposal and a manifest ceiling do not look alike and the card must not
 * pretend they do.
 *
 * The argument the screen has to make, restated for all three:
 *
 * **A request granted nothing, and approving is the write.** The queue is a
 * list of proposals; the button is the write. So every approve dialog shows
 * what approving would do, in the kernel's own words, before the click
 * rather than after it.
 *
 * **Object types and sync mappings do not trim.** A manifest ceiling has a
 * wider-or-narrower axis an approval can move along — hence `ApproveDialog`'s
 * checkboxes. A type declaration or a table mapping does not: the kernel
 * approves either exactly what was proposed or rejects it and says why (see
 * `object_types.py` and `sync_maps.py`'s own module docstrings), so their
 * approve dialogs are read-only proposals with one button, not a form.
 *
 * **A rejection needs a reason and the queue keeps it**, for all three —
 * a queue that forgets its rejections is handed the same request next week
 * with no way to know it is the same one.
 *
 * **Whether *you* may decide is answered before you click.** Reading any of
 * the three queues needs only the matching declared action
 * (`manifest.review`, `objecttype.approve`, `connector.map.approve`);
 * deciding one additionally needs the caller to be on this kernel's governor
 * list — the same list for all three, so "you may decide these" is one fact
 * checked once and reused everywhere it applies.
 */

/**
 * Long unbreakable strings are the entire content of this screen.
 *
 * Client ids, ULIDs, repository URLs and email addresses have no break
 * opportunity in them, and every one of them is rendered in `<code>`. At
 * 390px a single repo URL took the page body to 683px — found exactly the way
 * the last three of these were, by measuring `scrollWidth` against
 * `clientWidth` rather than by looking at it.
 *
 * `anywhere` rather than `break-word` because `break-word` still refuses to
 * break inside a word when the line has any other option, which is not the
 * case being fixed here.
 *
 * Applied to containers rather than to each `<code>`, and repeated on the
 * dialogs because a MUI `Dialog` renders in a portal at body level and
 * inherits nothing from the section it was opened from.
 */
const WRAPS = { "& code": { overflowWrap: "anywhere" } };

function Label({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {children}
    </Typography>
  );
}

/**
 * Who a request came from, read the same signal Activity's own `Mode` chip
 * already reads off an audit row: whether the token that made the call
 * carried a username claim. The kernel stamps `requested_by` as
 * `user:<username>` or `app:<instance>` for exactly this reason — it is not
 * this screen inferring "no person," it is the kernel saying so, the same
 * way it says so for `mode: unattended` elsewhere (LO-13x).
 *
 * A blank `requested_by` (a row written before the field existed) reads as
 * neither, not as unattended — an absence is not a value, and guessing here
 * would put back the exact inference the field was added to remove.
 */
function requestOrigin(request) {
  const by = String(request?.requested_by || "");
  if (by.startsWith("user:")) {
    return { unattended: false, actor: by.slice("user:".length) || "unknown" };
  }
  if (by.startsWith("app:")) {
    return { unattended: true, actor: by.slice("app:".length) || "unknown" };
  }
  return { unattended: false, actor: "" };
}

/**
 * Same word, same colour as Activity's own `Mode` chip for `mode:
 * unattended` — one vocabulary for "no person was there," not two that a
 * governor has to learn are the same fact.
 */
function UnattendedChip() {
  return (
    <Tooltip title="No person is behind this request — the token that filed it carried no username claim.">
      <Chip
        size="small"
        variant="outlined"
        icon={<SmartToyOutlinedIcon fontSize="small" />}
        label="unattended"
        aria-label="unattended: no person behind this request"
        sx={{ color: "kernel.unattended", borderColor: "kernel.unattended" }}
      />
    </Tooltip>
  );
}

/** Colour is never the only signal: every state carries a word. */
const STATE_STYLE = {
  pending: { colour: "kernel.attention", word: "pending" },
  approved: { colour: "kernel.permitted", word: "approved" },
  rejected: { colour: "kernel.refused", word: "rejected" },
  // Closed without a decision: a newer request replaced it, or whoever
  // asked withdrew it. Neither can be approved.
  superseded: { colour: "kernel.stale", word: "replaced" },
  cancelled: { colour: "kernel.stale", word: "cancelled" },
};

const CLOSED_WORD = {
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Replaced by a newer request",
  cancelled: "Cancelled",
};

function StateChip({ state }) {
  const style = STATE_STYLE[state] || { colour: "kernel.stale", word: state };
  return (
    <Chip
      size="small"
      variant="outlined"
      label={style.word}
      aria-label={`state: ${style.word}`}
      sx={{ color: style.colour, borderColor: style.colour, fontWeight: 600 }}
    />
  );
}

/**
 * What kind of proposal a card holds, said in words rather than left to be
 * inferred from its shape. A manifest ceiling, a new kind of record and a
 * table-to-type mapping are three different documents with three different
 * consequences, and a reviewer scanning a merged queue needs to know which
 * one they are looking at before they read a word further.
 */
const KIND_META = {
  manifest: { label: "app manifest", action: "manifest.review" },
  object_type: { label: "object type", action: "objecttype.approve" },
  syncmap: { label: "sync mapping", action: "connector.map.approve" },
  map_override: { label: "mapping override", action: "connector.map_override.approve" },
};

/** The identifying heading for a card or a dialog, one per kind. */
function titleFor(kind, request) {
  const d = request?.declaration || {};
  if (kind === "object_type") return <code>{d.name}</code>;
  if (kind === "syncmap") {
    return (
      <code>
        {d.source}/{d.table}
      </code>
    );
  }
  if (kind === "map_override") {
    return (
      <code>
        {d.source}/{d.concept}
      </code>
    );
  }
  return <code>{request?.app_type}</code>;
}

/**
 * The two chips that only ever apply to a manifest request. Kept out of the
 * generic card header so a governed-type or sync-map card is not carrying
 * markup for a case that can never be true of it.
 */
function ManifestChips({ request, sandbox }) {
  return (
    <>
      {sandbox ? (
        // The state is the word on the chip; the colour only marks the three
        // that are a live condition somebody should look at (LO-190).
        <Tooltip title="Where this request's sandbox is. It is on this request and listed nowhere else: the Apps screen, People, Objects, Activity and the counts do not include it.">
          <Chip
            size="small"
            variant="outlined"
            label={sandbox.label}
            sx={
              ["stale", "failed", "unknown"].includes(sandbox.state)
                ? { color: "kernel.attention", borderColor: "kernel.attention" }
                : undefined
            }
          />
        </Tooltip>
      ) : null}
      {request.approval_pending ? (
        // A governor's narrowed approval is recorded and has granted nothing; the
        // word says where its re-run is, and the colour only marks the two that
        // need somebody (LO-191).
        <Tooltip title="A governor approved this with lines struck. The decision is recorded and grants nothing until a sandbox run passes for the narrowed manifest.">
          <Chip
            size="small"
            variant="outlined"
            label={{
              waiting: "approval recorded · sandbox re-testing",
              failed: "approval recorded · re-test failed",
              blocked: "approval recorded · cannot land",
            }[request.approval_pending.phase] || "approval recorded"}
            sx={
              ["failed", "blocked"].includes(request.approval_pending.phase)
                ? { color: "kernel.attention", borderColor: "kernel.attention" }
                : undefined
            }
          />
        </Tooltip>
      ) : null}
      {request.replaces_version ? (
        <Tooltip title="This app type already has a ceiling this console approved. Approving replaces it wholesale.">
          <Chip
            size="small"
            variant="outlined"
            label={`replaces v${request.replaces_version}`}
            sx={{ color: "kernel.attention", borderColor: "kernel.attention" }}
          />
        </Tooltip>
      ) : null}
      {request.trimmed ? (
        <Tooltip title="Approved with lines struck from what was asked for. Both are shown below.">
          <Chip size="small" variant="outlined" label="trimmed" />
        </Tooltip>
      ) : null}
    </>
  );
}

/**
 * One `reads` or `service_reads` block, read-only.
 *
 * The `permits` sentence comes from the kernel and is shown rather than
 * summarised, because it carries the rule people get backwards. Empty is a
 * real answer with something specific to say, so it is a sentence and not a
 * blank.
 */
function Ceiling({ grants, empty }) {
  if (!grants?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {grants.map((g) => (
        <Box key={g.type}>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {g.type}
            </Typography>
            {g.tags.length ? (
              g.tags.map((t) => (
                <Chip key={t} size="small" variant="outlined" label={t} />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                untagged only
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {g.permits}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * The actions a request asks for.
 *
 * A control-plane verb is marked and carries the kernel's sentence about what
 * it does. `manifest.review` and `organization.update` rendered identically
 * would be a queue that buried the one that matters — an app asking for
 * `manifest.review` is asking to decide what every other app may read.
 */
function Actions({ actions, empty }) {
  if (!actions?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {actions.map((a) => (
        <Stack
          key={a.verb}
          direction="row"
          spacing={0.75}
          sx={{ alignItems: "baseline", flexWrap: "wrap" }}
        >
          <Chip
            size="small"
            variant="outlined"
            label={a.control_plane ? `⚑ ${a.verb}` : a.verb}
            aria-label={
              a.control_plane
                ? `${a.verb} — control plane`
                : `${a.verb} — object write`
            }
            sx={{
              fontFamily: "monospace",
              color: a.control_plane ? "kernel.attention" : undefined,
              borderColor: a.control_plane ? "kernel.attention" : undefined,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
            {a.detail}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * The agents a request asks to declare (LO-81).
 *
 * Shown here because a governor who cannot see them is approving them blind,
 * and an agent name lands in every audit row that app writes. It is not a
 * permission and the caption says so — but it is attribution, which is a
 * claim about who did something, and a claim nobody reviewed is worth less
 * than no claim.
 *
 * Grouped by nothing. Division is a free string on the manifest, and the
 * grouping argument belongs on the Apps screen where the *declared* ones
 * live; a proposal is short enough to read as a list.
 */
function ProposedAgents({ agents }) {
  if (!agents?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        The request declares no agents. Every write from this app would be the
        app&apos;s own code.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {agents.map((agent) => (
        <Box key={agent.name} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ alignItems: "baseline", flexWrap: "wrap" }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={agent.name}
              sx={{ fontFamily: "monospace", maxWidth: "100%" }}
            />
            <Typography variant="caption" color="text.secondary">
              {agent.division}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", overflowWrap: "anywhere" }}
          >
            {agent.does}
          </Typography>
          {agent.instructions ? (
            // The prompt the platform will create this agent from (LO-128).
            // Shown whole, because it is what the agent will try to do every
            // time it runs, and approving it is approving that.
            <Typography
              variant="caption"
              component="pre"
              sx={{
                display: "block", whiteSpace: "pre-wrap", overflowWrap: "anywhere",
                mt: 0.5, p: 1, borderRadius: 1, bgcolor: "action.hover",
                fontFamily: "monospace",
              }}
            >
              {agent.instructions}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Stack>
  );
}

/**
 * The hosts a request asks its app's server function to call (LO-175), as the
 * exact names they were declared with.
 *
 * Said as a list of what was *declared*, with the caption that it is not a
 * record of what the app does: this screen has no observation to show, and a
 * list of hosts rendered as though somebody had watched the traffic would be
 * the overstatement the kit's README is about.
 */
function Hosts({ hosts, empty }) {
  if (!hosts?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Stack
      direction="row"
      sx={{ gap: 0.75, flexWrap: "wrap" }}
      role="list"
      aria-label="declared egress hosts"
    >
      {hosts.map((host) => (
        <Chip
          key={host}
          role="listitem"
          size="small"
          variant="outlined"
          label={host}
          // Wrapped, not clipped: a host is read letter by letter, and an
          // ellipsis hides the part somebody is checking.
          sx={{
            fontFamily: "monospace",
            maxWidth: "100%",
            height: "auto",
            "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: 0.25 },
          }}
        />
      ))}
    </Stack>
  );
}

/**
 * The aggregates a request declares (LO-58), each as what it computes.
 *
 * An aggregate is the one declaration that reaches past the user half of the
 * intersection: the app receives a figure over records the person could not
 * open. It is approved separately from read scope and it is shown here for the
 * reason every other line is: a governor who cannot see it is approving it blind.
 */
function Aggregates({ aggregates }) {
  if (!aggregates?.length) return null;
  return (
    <Stack spacing={0.5}>
      {aggregates.map((a) => (
        <Box key={a.name} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600, overflowWrap: "anywhere" }}>
            {a.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
            {describeAggregate(a)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * Declared egress beside what the sandbox observed (LO-175, LO-185).
 *
 * The right-hand side is the gateway's own log, compared with the left (LO-180):
 * a host contacted and never declared is a failure, a host declared and never
 * contacted is a narrowing candidate, and an empty log says it is not a clean
 * one. A request with no sandbox has nothing observed and says that instead of
 * leaving an empty box, because "no hosts observed" would read as "it called
 * none".
 */
function EgressPanel({ ceiling, requestId, hasSandbox, part, reload }) {
  return (
    // One above the other: the observed side is a table, and a half-width column
    // breaks a host name in the middle of a word (LO-185).
    <Stack direction="column" spacing={2} sx={{ alignItems: "stretch" }}>
      <Box sx={{ minWidth: 0, flex: 1, maxWidth: "100%" }}>
        <Label>Would call — hosts declared</Label>
        <Hosts
          hosts={ceiling.egress}
          empty="None. The app's server function asks to call no host outside the kernel."
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Exact hostnames, as declared by whoever asked. You can strike a host
          when approving and never add one, and a real install is held to what
          you approve.
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, maxWidth: "100%" }}>
        <Label>Observed in the sandbox</Label>
        <ObservedEgress
          part={part}
          declared={ceiling.egress}
          hasSandbox={hasSandbox}
          requestId={requestId}
          reload={reload}
        />
      </Box>
    </Stack>
  );
}

/**
 * A request's sandbox, shown on the request it belongs to and nowhere else
 * (LO-190).
 *
 * The chip in the card's header says the state in words. This is what stands
 * behind it: why it is in that state, a way to open it when there is something
 * to open, and what a running sandbox is not. It renders nothing for a request
 * with no sandbox, because "no sandbox" is not a finding about the app.
 */
function SandboxStatus({ sandbox, token, personas }) {
  if (!sandbox) return null;
  // The approver's own access token, in the fragment, is what lets the kit
  // running in the sandbox sign a persona in on their behalf (LO-188). It is
  // read from the fragment there and taken out of the address bar at once.
  const href = liveLink(sandbox.appUrl, token) || sandbox.appUrl;
  const names = (personas || []).map((p) => p.name).filter(Boolean);
  return (
    <>
      <Divider sx={{ my: 1.5 }} />
      <Label>Sandbox</Label>
      <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {sandbox.detail}
        </Typography>
        {href ? (
          <>
            <Button
              size="small"
              variant="outlined"
              href={href}
              target="_blank"
              rel="noreferrer"
            >
              Open the sandbox, signed in as you
            </Button>
            <Typography variant="caption" color="text.secondary">
              This link carries your own access token after the <code>#</code>, so the
              sandbox can sign a persona in for you. The token is yours and lasts about
              an hour: do not paste the link anywhere. Once it opens, the bar at the
              bottom of the page signs you in as {names.length
                ? <>one of <code>{names.join(", ")}</code></>
                : "a persona"}
              {" "}— the persona&apos;s own credentials are never in the link.
            </Typography>
          </>
        ) : null}
        <Typography variant="caption" color="text.secondary">
          It runs this build on synthetic data, under the ceiling being asked
          for and not yet approved. It is not an installed app, and being up
          says nothing about whether the app is right.
        </Typography>
      </Stack>
    </>
  );
}

/**
 * Who the sandbox signs in as and what it is seeded with (LO-175), read-only.
 *
 * An approver does not edit these: they are the author's, they never reach the
 * manifest, and there is no control here for that reason. They are shown so a
 * pass can be read for what it tried — an app that was only ever tested as a
 * person who holds every tag has not been tested as anybody else. The queue
 * carries counts for the records (the records themselves are 100 KB a request
 * and are read on the request), so counts are what is said.
 */
function SandboxRequest({ proposed }) {
  const personas = proposed.personas || [];
  const counts = Object.entries(proposed.fixture_counts || {});
  const records = counts.reduce((n, [, count]) => n + count, 0);
  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 4 }}
        sx={{ alignItems: "flex-start" }}
      >
        <Box sx={{ minWidth: 0, flex: 1, maxWidth: "100%" }}>
          <Label>People it is tried as ({personas.length})</Label>
          {personas.length ? (
            <Stack spacing={0.75}>
              {personas.map((p) => (
                <Stack
                  key={p.name}
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "center", flexWrap: "wrap" }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600, overflowWrap: "anywhere" }}
                  >
                    {p.name}
                  </Typography>
                  {p.tags?.length ? (
                    p.tags.map((t) => (
                      <Chip key={t} size="small" variant="outlined" label={t} />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      holds no tags
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None declared.
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, maxWidth: "100%" }}>
          <Label>Sample records it is seeded with ({records})</Label>
          {counts.length ? (
            <Stack
              direction="row"
              sx={{ gap: 0.75, flexWrap: "wrap" }}
            >
              {counts.map(([type, count]) => (
                <Chip
                  key={type}
                  size="small"
                  variant="outlined"
                  label={`${type} · ${count}`}
                  sx={{ fontFamily: "monospace" }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None declared. It is seeded from the data model alone.
            </Typography>
          )}
        </Box>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        For the sandbox only: approving writes neither to the manifest and
        grants nothing to them. A pass against them says the app behaved for
        these people and these records, not for yours. Counts are shown here;
        the records are on the request itself.
      </Typography>
    </Stack>
  );
}

/** The two declarations, side by side, never summed. */
function Proposal({ ceiling, egress = true }) {
  const wider = ceiling.service_wider_than_user || [];
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 2, md: 4 }}
        sx={{ alignItems: "flex-start" }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Label>Would read — with a user</Label>
          <Ceiling
            grants={ceiling.reads}
            empty="Nothing. This app would read no type on behalf of a signed-in person."
          />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Label>Would read — with no user behind it</Label>
          <Ceiling
            grants={ceiling.service_reads}
            empty="Nothing. This app would read only on behalf of a signed-in person, which is the narrower and more usual case."
          />
        </Box>
      </Stack>

      {wider.length ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>
            Would reach further as a job than for a person: {wider.join(", ")}
          </AlertTitle>
          With no user behind a request there is no second limit — only what
          the app asked for. Approving this lets the app see more of{" "}
          {wider.length === 1 ? "that type" : "those types"} unattended than
          any signed-in person would get through it. Legitimate for a nightly
          job, and the thing to ask about before saying yes.
        </Alert>
      ) : null}

      <Box>
        <Label>Would write</Label>
        <Actions
          actions={ceiling.actions}
          empty="Nothing. This app would read and never write."
        />
      </Box>

      {ceiling.service_actions?.length ? (
        <Box>
          <Label>Would do — with no user behind it</Label>
          <Actions actions={ceiling.service_actions} empty="" />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            What its timer or its agent may do unattended. Judged by this list
            alone — there is no person to intersect with.
          </Typography>
        </Box>
      ) : null}

      {ceiling.aggregates?.length ? (
        <Box>
          <Label>Would compute — aggregates</Label>
          <Aggregates aggregates={ceiling.aggregates} />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            The app receives a figure and never the rows, and may receive it
            over records the signed-in person could not open. That is why it is
            approved on its own line.
          </Typography>
        </Box>
      ) : null}

      <Box>
        <Label>Would declare — agents</Label>
        <ProposedAgents agents={ceiling.agents} />
        {ceiling.agents?.length ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            An agent is not a second identity and grants nothing. Approving
            these lets the app put these names — and only these names — on the
            records it writes, inside the ceiling above.
          </Typography>
        ) : null}
      </Box>

      {egress ? (
        <Box>
          <Label>Would call — egress hosts</Label>
          <Hosts
            hosts={ceiling.egress}
            empty="None. The app's server function may call no host outside the kernel."
          />
        </Box>
      ) : null}
    </Stack>
  );
}

/**
 * What a request would change about the ceiling an app has today (LO-128).
 *
 * A request that replaces a ceiling is reviewed as a whole document, and a
 * whole document hides a one-word change: the first update through the MCP
 * narrowed every read from three tags to untagged-only, said the permissions
 * were unchanged, and was approved — the card showed the new ceiling and not
 * the difference. So the difference is computed here and put first.
 */
const SIGN_WORD = { "+": "adds", "-": "drops", "~": "changes" };

/**
 * One line of a diff, marked by its sign AND a word, so the mark is never colour alone.
 * Added lines carry a tint of the attention colour, because an added grant or host is
 * what the approver is being asked to allow.
 */
function DiffLine({ sign, children }) {
  return (
    <Typography
      variant="body2"
      component="div"
      sx={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        px: 0.5,
        bgcolor: sign === "+"
          ? "color-mix(in srgb, var(--mui-palette-kernel-attention, #b26a00) 14%, transparent)"
          : "transparent",
      }}
    >
      {sign}
      {" "}
      {children}
    </Typography>
  );
}

function Changes({ current, proposed }) {
  const diff = ceilingDiff(current, proposed);
  return (
    <Alert severity={diff.lines.length ? "warning" : "info"} variant="outlined" sx={{ mt: 1.5 }}>
      <AlertTitle>
        {diff.lines.length
          ? `Changes from the ceiling it has today (v${current.version}): `
            + `${diff.added} added, ${diff.removed} removed, ${diff.changed} changed`
          : `No change to what it may read or do (v${current.version})`}
      </AlertTitle>
      {diff.lines.map((line) => (
        <DiffLine sign={line.sign} key={`${line.sign}${line.axis}${line.text}`}>
          {line.axis}: {SIGN_WORD[line.sign]} {line.text}
        </DiffLine>
      ))}
      {diff.widens ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Lines marked + are new: approving lets the app read or reach something
          the version in force cannot. Against the manifest in force, not against
          what the app does with it.
        </Typography>
      ) : null}
    </Alert>
  );
}

/**
 * The code a second version changes, against the build that is live (LO-203).
 *
 * Which build is live is read from the control plane (the running install and the
 * release it pins) and the publisher's builds (which build that release came from),
 * and both sources are read from the publisher. Every step that could not be read is
 * said as that: no install, a release no build here made, a source that did not load.
 * The summary is on the card; the files are one click in.
 */
function CodeChanges({ request, get, token }) {
  const app = request?.app_type;
  const buildId = request?.proposed?.build_id;
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!app || !buildId || !token) return undefined;
    let live = true;
    (async () => {
      let instances = null;
      let builds = null;
      try {
        instances = (await controlGet(token)("/instances"))?.instances || [];
      } catch {
        instances = null;
      }
      try {
        builds = (await get(`/builds/${app}`))?.builds || [];
      } catch {
        builds = null;
      }
      const running = liveBuildOf({ app, instances, builds });
      if (!running.known || !running.buildId || running.buildId === buildId) {
        if (live) setState({ running });
        return;
      }
      try {
        const [was, now] = await Promise.all([
          get(`/builds/${app}/${running.buildId}/source`),
          get(`/builds/${app}/${buildId}/source`),
        ]);
        if (live) setState({ running, diff: sourceDiff(was?.files, now?.files) });
      } catch (e) {
        if (live) setState({ running, failed: e?.body?.detail || e?.message || "not read" });
      }
    })();
    return () => {
      live = false;
    };
  }, [app, buildId, token, get]);

  if (!buildId) return null;
  if (!state) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Reading the live build to compare the code with…
      </Typography>
    );
  }
  const { running, diff, failed } = state;
  let summary;
  if (!running.known) summary = "Code: the live build could not be read, so the code is not compared.";
  else if (!running.instance) summary = "Code: not installed anywhere, so there is no live build to compare with.";
  else if (!running.buildId) {
    summary = `Code: ${running.instance} runs ${running.version}, a release no build here made, so there is no source to compare with.`;
  } else if (running.buildId === buildId) summary = "Code: this is the build that is live.";
  else if (failed) summary = `Code: the sources did not load (${failed}), so the code is not compared.`;
  else {
    summary = `Code: ${diff.files.length} file${diff.files.length === 1 ? "" : "s"} changed, `
      + `${diff.added} line${diff.added === 1 ? "" : "s"} added and ${diff.removed} removed, against build `
      + `${running.buildId} (version ${running.version}, live as ${running.instance}).`;
  }
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {summary}
      </Typography>
      {diff?.files?.length ? (
        <Disclose label="The changed lines" summary="Added lines are marked +, removed lines -." count={diff.files.length}>
          <Box sx={{ maxHeight: "28rem", overflow: "auto", mt: 1, border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
            {diff.files.map((file) => (
              <Box key={file.path} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                  {file.path}: {file.status}
                  {file.tooLarge ? ", too large to show line by line" : `, +${file.added} -${file.removed}`}
                </Typography>
                {file.lines.filter((l) => l.sign !== " ").map((l, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <DiffLine sign={l.sign} key={i}>{l.text}</DiffLine>
                ))}
              </Box>
            ))}
          </Box>
        </Disclose>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        The sources each build was made from. Not what the build resolved for its
        dependencies, and not a statement that the change is safe.
      </Typography>
    </Box>
  );
}

/** Both differences a second version brings, for the card and for the approve window. */
function VersionChanges({ request, current, get, token }) {
  return (
    <>
      <Changes current={current} proposed={request.proposed} />
      <CodeChanges request={request} get={get} token={token} />
    </>
  );
}

/** Everything a manifest card shows beneath its header — the proposal, and,
 * once decided, what was actually granted. Split out of `RequestCard` so the
 * card itself carries nothing specific to any one kind. */
function ManifestBody({
  request, current, sandbox, evidence, get, token, reload,
}) {
  const origin = requestOrigin(request);
  // `evidence` is what the three sandbox reads have returned so far. Whether
  // there is a sandbox to read is a fact about the request, so a read that has
  // not landed shows as loading rather than as no sandbox.
  const hasSandbox = wantsSandbox(request);
  const reads = evidence || {};
  // A narrative from an unattended agent reads as a change report, not a
  // one-line justification — "found X, read the source, changed exactly
  // these call sites" — and blending it into the same plain paragraph a
  // person's own "why I want this" gets loses that at a glance. Only when
  // there is a build behind it: an unattended request with no `build_id`
  // is not a fix report, it is something else entirely and gets no claim
  // to being one.
  const isAgentFix = origin.unattended && Boolean(request.proposed.build_id);
  return (
    <>
      {request.proposed.access ? (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          <strong>Who can use it:</strong>{" "}
          {request.proposed.access.mode === "listed"
            ? `only ${(request.proposed.access.users || []).join(", ") || "the person asking"}, plus the person asking`
            : "everyone in the organization"}
          . Approving sets this; the requester can change it afterwards and a
          governor can override them.
        </Typography>
      ) : null}
      {current && request.state === "pending" ? (
        <VersionChanges request={request} current={current} get={get} token={token} />
      ) : null}
      <Box sx={{ mt: 1.5 }}>
        {isAgentFix ? (
          <Alert
            severity="info"
            variant="outlined"
            icon={<SmartToyOutlinedIcon fontSize="small" />}
          >
            <AlertTitle>
              What <code>{request.requested_by_app}</code> found and changed
            </AlertTitle>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {request.proposed.purpose}
            </Typography>
          </Alert>
        ) : (
          <>
            <Label>Why they want it</Label>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {request.proposed.purpose}
            </Typography>
          </>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Contact <code>{request.proposed.contact}</code>
          {request.proposed.repo ? (
            <>
              {" · "}repo <code>{request.proposed.repo}</code>
            </>
          ) : null}
          . Declared by whoever asked, and no more checked than that.
        </Typography>
        {request.proposed.build_id ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Code: build <code>{request.proposed.build_id}</code>. Approving
            releases and installs exactly that build — and only if it
            succeeded; a failed one is refused at release, after the ceiling
            is written.
          </Typography>
        ) : null}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Label>
        {request.state === "approved"
          ? "What was asked for"
          : "What approving this would permit"}
      </Label>
      <Proposal ceiling={request.proposed} egress={false} />

      <SandboxStatus
        sandbox={sandbox}
        token={token}
        personas={request.proposed.personas}
      />

      {/* The sandbox is gone once a request is decided; what the decision
          relied on was frozen first, and is shown read-only (LO-199). */}
      {wantsEvidence(request) ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <DecisionEvidence request={request} get={get} />
        </>
      ) : null}

      {hasSandbox ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <QaReport request={request} part={reads.qa} get={get} reload={reload} />
          <Divider sx={{ my: 1.5 }} />
          <CallLog request={request} part={reads.calls} get={get} reload={reload} />
        </>
      ) : null}

      <Divider sx={{ my: 1.5 }} />

      <EgressPanel
        ceiling={request.proposed}
        requestId={request.request_id}
        hasSandbox={hasSandbox}
        part={reads.egress}
        reload={reload}
      />

      <Divider sx={{ my: 1.5 }} />

      <Label>What the sandbox is asked to test with</Label>
      <SandboxRequest proposed={request.proposed} />

      {request.approved ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Label>What was granted — version {request.approved.version}</Label>
          <Proposal ceiling={request.approved} />
        </>
      ) : null}
    </>
  );
}

/**
 * A proposed object type, read for a reviewer rather than as raw JSON: its
 * name and collection, whether it is sensitive, and every declared field
 * with its type and whether it is required.
 */
function ObjectTypeBody({ request }) {
  const d = request.declaration || {};
  const fields = d.fields || [];
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Chip
          size="small"
          variant="outlined"
          label={`collection · ${d.collection}`}
          sx={{ fontFamily: "monospace" }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={d.sensitive ? "sensitive" : "not sensitive"}
          sx={
            d.sensitive
              ? { color: "kernel.attention", borderColor: "kernel.attention" }
              : undefined
          }
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Approving writes this as a real kind of record: from the next{" "}
        <code>GET /schema</code>, every app sees <code>{d.name}</code> exactly
        as a built-in type. It does not let anything create a record of one —
        that is a separate decision, a sync mapping, below.
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ border: 0, fontWeight: 600 }}>Field</TableCell>
              <TableCell sx={{ border: 0, fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ border: 0, fontWeight: 600 }}>Required</TableCell>
            </TableRow>
            {fields.map((f) => (
              <TableRow key={f.name}>
                <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                  {f.name}
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  {f.minor_units
                    ? `${f.type} · minor units, currency in ${f.currency_field}`
                    : f.type}
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  {f.required ? "required" : "optional"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

/**
 * A proposed sync mapping, read for a reviewer: which table becomes which
 * type, the column-to-field renames it would apply, and the tag it would
 * stamp on every record it produces, if any.
 */
function SyncMapBody({ request }) {
  const d = request.declaration || {};
  const pairs = Object.entries(d.field_map || {});
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Chip
          size="small"
          variant="outlined"
          label={`target · ${d.target_type}`}
          sx={{ fontFamily: "monospace" }}
        />
        {d.tag ? (
          <Chip size="small" variant="outlined" label={`tag · ${d.tag}`} />
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          label={d.enabled === false ? "requested off" : "requested on"}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Approving points <code>{d.source}</code>&apos;s <code>{d.table}</code>{" "}
        table at <code>{d.target_type}</code>. It does not run a sync by
        itself — the next scheduled run reads it, and only once this
        mapping&apos;s own enabled flag and the connector&apos;s own cadence
        are both on.
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ border: 0, fontWeight: 600 }}>
                Source column
              </TableCell>
              <TableCell sx={{ border: 0, width: 30 }} />
              <TableCell sx={{ border: 0, fontWeight: 600 }}>
                Kernel field
              </TableCell>
            </TableRow>
            {pairs.map(([column, field]) => (
              <TableRow key={column}>
                <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                  {column}
                </TableCell>
                <TableCell sx={{ border: 0, color: "text.secondary" }}>
                  &rarr;
                </TableCell>
                <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                  {field}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

/**
 * A proposed mapping override, read for a reviewer (LO-172): which payload
 * field it fills, from what, when that field is blank — never what a
 * record's `fields()` output already is, because this patches the raw
 * payload `mapping.py`'s own unchanged function then reads.
 */
function MapOverrideBody({ request }) {
  const d = request.declaration || {};
  const rules = d.rules || [];
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Chip
          size="small"
          variant="outlined"
          label={`source · ${d.source}`}
          sx={{ fontFamily: "monospace" }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`concept · ${d.concept}`}
          sx={{ fontFamily: "monospace" }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Approving writes this as the live correction for <code>{d.source}</code>
        &apos;s <code>{d.concept}</code>. It does not touch anything already in
        the graph — it changes what the next run, or a replay, produces. A
        rule only fires when its field is blank: a record that already sends
        this field is untouched, approved or not.
      </Typography>
      {d.reason ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Why:</strong> {d.reason}
        </Typography>
      ) : null}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ border: 0, fontWeight: 600 }}>
                Payload field
              </TableCell>
              <TableCell sx={{ border: 0, width: 30 }} />
              <TableCell sx={{ border: 0, fontWeight: 600 }}>
                Filled from, when blank
              </TableCell>
              <TableCell sx={{ border: 0, fontWeight: 600 }}>
                Cleaned with
              </TableCell>
            </TableRow>
            {rules.map((r, i) => (
              <TableRow key={i}>
                <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                  {r.payload_path}
                </TableCell>
                <TableCell sx={{ border: 0, color: "text.secondary" }}>
                  &rarr;
                </TableCell>
                <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                  {r.source_path ? r.source_path : `literal "${r.literal}"`}
                </TableCell>
                <TableCell sx={{ border: 0 }}>{r.clean || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

/** Which component reads a non-manifest kind's declaration, shared by the
 * card body and the approve dialog so the two never drift apart. */
const NON_MANIFEST_BODY = {
  object_type: ObjectTypeBody,
  syncmap: SyncMapBody,
  map_override: MapOverrideBody,
};

/** The generic approve dialog's intro line, one per non-manifest kind — what
 * approving actually does, in words, before the same document the card
 * already shows is repeated below it. */
const NON_MANIFEST_INTRO = {
  object_type: (
    <>
      Approving writes this type. From the next <code>GET /schema</code>,
      every app sees it exactly as a built-in type. It does not let
      anything populate it — that is a separate, sync-mapping, decision,
      and nothing here creates a record of this type.
    </>
  ),
  syncmap: (
    <>
      Approving points this table at its target type. It does not run a
      sync — the next scheduled run is what actually reads it, and only if
      this mapping&apos;s own enabled flag and the connector&apos;s own
      cadence are both on. {SYNCMAP_CHARTABLE}
    </>
  ),
  map_override: (
    <>
      Approving makes this correction live. It does not touch anything
      already in the graph — it changes what the next run, or a replay,
      produces, and only for a record whose field is currently blank.
    </>
  ),
};

/**
 * Approve, with optional trimming.
 *
 * Everything starts ticked. Unticking is the only edit available, which makes
 * "an approval narrows and never widens" a property of the control rather
 * than a rule somebody has to remember — and the kernel refuses a widening
 * approval anyway, so a screen that could compose one would be offering a
 * button guaranteed to fail.
 *
 * A tag can be unticked on its own, because striking `vendor` from a grant of
 * Organization is a real and common trim and rejecting the whole request to
 * get it is the outcome this dialog exists to avoid.
 *
 * Manifest-only: object-type and sync-map approvals do not trim (see
 * `SimpleApproveDialog`).
 */
function ApproveDialog({
  request, narrow = NO_NARROWING, open, busy, refusal, stage, landed, get, current, token,
  onCancel, onConfirm, onStage, onLanded, onStopped, onReject,
}) {
  const proposed = request?.proposed;
  // What the kernel says the gate will do with a decision on this request, said
  // before a line is struck and never assumed (LO-191).
  const words = gateWords(request?.gate);
  // The button is disabled while the approve is in flight, and a disabled button
  // drops focus. When the gate refuses, the retry is that button, so focus goes
  // back to it: a keyboard user who has just heard why would otherwise have to
  // tab through every box in the dialog to press it again (LO-191).
  const approveButton = useRef(null);
  useEffect(() => {
    if (refusal && !busy) approveButton.current?.focus();
  }, [refusal, busy]);

  // The address this app will answer on, which the approver may change.
  //
  // ## Why the name is decided here
  //
  // The assistant proposes it, because the assistant is the one having the
  // conversation and knows what the app is for. But the person approving is
  // the one who has to live with the hostname, and until now they could not
  // touch it: the name fell out of the catalog row and the first time anybody
  // saw it was after it was already a certificate and a DNS record. Changing
  // it afterwards means installing again under a second name.
  //
  // It is free to change because every name lands in the same place. An
  // assistant-built app is `app-<something>` and the provisioner is granted
  // `app-*` once, so renaming picks a different name inside a namespace that
  // is already permitted rather than asking for a new grant.
  const [displayName, setDisplayName] = useState("");

  const [keptTags, setKeptTags] = useState({});
  const [keptActions, setKeptActions] = useState({});
  const [keptAgents, setKeptAgents] = useState({});
  // Hosts, struck like a read or a verb: ticked is kept, and there is no way to
  // add one, which is the rule the kernel enforces with `would_widen` (LO-175).
  const [keptEgress, setKeptEgress] = useState({});
  // The two the approve route has always taken a replacement for and this dialog
  // did not offer (LO-191): writes with no user behind them, and aggregates.
  const [keptServiceActions, setKeptServiceActions] = useState({});
  const [keptAggregates, setKeptAggregates] = useState({});

  // Every line ticked, which is "approve exactly what was asked for".
  function tickEverything() {
    if (!proposed) return;
    setKeptEgress(
      Object.fromEntries((proposed.egress || []).map((host) => [host, true])),
    );
    const tags = {};
    for (const key of ["reads", "service_reads"]) {
      for (const g of proposed[key] || []) {
        tags[`${key}:${g.type}`] = { type: true, tags: new Set(g.tags) };
      }
    }
    setKeptTags(tags);
    setKeptActions(
      Object.fromEntries((proposed.actions || []).map((a) => [a.verb, true])),
    );
    setKeptServiceActions(
      Object.fromEntries((proposed.service_actions || []).map((a) => [a.verb, true])),
    );
    setKeptAggregates(
      Object.fromEntries((proposed.aggregates || []).map((a) => [a.name, true])),
    );
    setKeptAgents(
      Object.fromEntries((proposed.agents || []).map((a) => [a.name, true])),
    );
  }

  // Reset every time a different request is opened, so a trim made on one
  // request cannot leak into the next one.
  useEffect(() => {
    tickEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposed]);

  // Proposed from the app's own name, and reset per request for the same
  // reason the trims are: a name typed against one request must not arrive
  // on the next one.
  useEffect(() => {
    if (!request?.app_type) return;
    setDisplayName(titleOf(request.app_type));
  }, [request?.app_type]);

  const slug = slugOf(displayName);

  const trimmed = useMemo(() => {
    if (!proposed) {
      return { reads: [], service_reads: [], actions: [], agents: [], egress: [] };
    }
    const out = { actions: [] };
    for (const key of ["reads", "service_reads"]) {
      out[key] = (proposed[key] || [])
        .filter((g) => keptTags[`${key}:${g.type}`]?.type)
        .map((g) => ({
          type: g.type,
          tags: g.tags.filter((t) => keptTags[`${key}:${g.type}`].tags.has(t)),
        }));
    }
    out.actions = (proposed.actions || [])
      .map((a) => a.verb)
      .filter((verb) => keptActions[verb]);
    // Whole declarations, kept or struck. There is no half-approving an
    // agent: the kernel compares the three fields together, because a
    // `groomer` approved with a different sentence or a different division is
    // a different declaration from the one that was proposed.
    out.agents = (proposed.agents || []).filter((a) => keptAgents[a.name]);
    // Always sent, as the other lists are: present means "exactly these",
    // absent means "as requested", and what is on screen is what is approved.
    out.egress = (proposed.egress || []).filter((host) => keptEgress[host]);
    // Sent only when one is struck. Absent is "as requested" and a full list is
    // the same statement, so this changes no approval; it keeps an untouched
    // approval's body what it was before these two had boxes, and the
    // aggregates are echoed whole only when there is a reason to.
    if ((proposed.service_actions || []).some((a) => !keptServiceActions[a.verb])) {
      out.service_actions = (proposed.service_actions || [])
        .map((a) => a.verb)
        .filter((verb) => keptServiceActions[verb]);
    }
    if ((proposed.aggregates || []).some((a) => !keptAggregates[a.name])) {
      out.aggregates = (proposed.aggregates || []).filter((a) => keptAggregates[a.name]);
    }
    return out;
  }, [proposed, keptTags, keptActions, keptAgents, keptEgress, keptServiceActions, keptAggregates]);

  const struck = useMemo(() => {
    if (!proposed) return 0;
    let n = 0;
    for (const key of ["reads", "service_reads"]) {
      for (const g of proposed[key] || []) {
        const kept = keptTags[`${key}:${g.type}`];
        if (!kept?.type) {
          n += 1;
          continue;
        }
        n += g.tags.filter((t) => !kept.tags.has(t)).length;
      }
    }
    n += (proposed.actions || []).filter((a) => !keptActions[a.verb]).length;
    n += (proposed.service_actions || []).filter((a) => !keptServiceActions[a.verb]).length;
    n += (proposed.aggregates || []).filter((a) => !keptAggregates[a.name]).length;
    n += (proposed.agents || []).filter((a) => !keptAgents[a.name]).length;
    n += (proposed.egress || []).filter((host) => !keptEgress[host]).length;
    return n;
  }, [proposed, keptTags, keptActions, keptAgents, keptEgress, keptServiceActions, keptAggregates]);

  const serviceKept = (proposed?.service_actions || []).filter((a) => keptServiceActions[a.verb]).length;
  const nothing =
    !trimmed.reads.length &&
    !trimmed.service_reads.length &&
    !trimmed.actions.length &&
    !serviceKept;

  // An aggregate kept over a read that was struck: the kernel refuses it, since
  // an aggregate may reach past the user and never past the app. Said before the
  // button rather than after it.
  const orphans = useMemo(
    () => orphanedAggregates(
      proposed?.aggregates,
      new Set((trimmed.reads || []).map((g) => g.type)),
      keptAggregates,
    ),
    [proposed, trimmed, keptAggregates],
  );

  // The lines the sandbox did not use (LO-185), and how many of them are still
  // ticked. Flagged beside the box that strikes each one, and struck together
  // only when the approver presses the button: the default is nothing struck.
  const unusedHere = useMemo(() => {
    if (!proposed) return 0;
    let n = 0;
    for (const key of ["reads", "service_reads"]) {
      n += (proposed[key] || []).filter((g) => narrow[key].has(g.type)).length;
    }
    n += (proposed.actions || []).filter((a) => narrow.actions.has(a.verb)).length;
    n += (proposed.service_actions || []).filter((a) => narrow.service_actions.has(a.verb)).length;
    n += (proposed.aggregates || []).filter((a) => narrow.aggregates.has(a.name)).length;
    n += (proposed.egress || []).filter((host) => narrow.egress.has(host)).length;
    return n;
  }, [proposed, narrow]);

  const unusedKept = useMemo(() => {
    if (!proposed) return 0;
    let n = 0;
    for (const key of ["reads", "service_reads"]) {
      n += (proposed[key] || []).filter(
        (g) => narrow[key].has(g.type) && keptTags[`${key}:${g.type}`]?.type,
      ).length;
    }
    n += (proposed.actions || []).filter(
      (a) => narrow.actions.has(a.verb) && keptActions[a.verb],
    ).length;
    n += (proposed.service_actions || []).filter(
      (a) => narrow.service_actions.has(a.verb) && keptServiceActions[a.verb],
    ).length;
    n += (proposed.aggregates || []).filter(
      (a) => narrow.aggregates.has(a.name) && keptAggregates[a.name],
    ).length;
    n += (proposed.egress || []).filter(
      (host) => narrow.egress.has(host) && keptEgress[host],
    ).length;
    return n;
  }, [proposed, narrow, keptTags, keptActions, keptEgress, keptServiceActions, keptAggregates]);

  function strikeUnused() {
    setKeptTags((prev) => {
      const next = { ...prev };
      for (const key of ["reads", "service_reads"]) {
        for (const type of narrow[key]) {
          const entry = next[`${key}:${type}`];
          if (entry) next[`${key}:${type}`] = { ...entry, type: false };
        }
      }
      return next;
    });
    setKeptActions((prev) => {
      const next = { ...prev };
      for (const verb of narrow.actions) if (verb in next) next[verb] = false;
      return next;
    });
    setKeptServiceActions((prev) => {
      const next = { ...prev };
      for (const verb of narrow.service_actions) if (verb in next) next[verb] = false;
      return next;
    });
    setKeptAggregates((prev) => {
      const next = { ...prev };
      for (const name of narrow.aggregates) if (name in next) next[name] = false;
      return next;
    });
    setKeptEgress((prev) => {
      const next = { ...prev };
      for (const host of narrow.egress) if (host in next) next[host] = false;
      return next;
    });
  }

  function toggleType(key, type) {
    setKeptTags((prev) => {
      const entry = prev[`${key}:${type}`];
      return { ...prev, [`${key}:${type}`]: { ...entry, type: !entry.type } };
    });
  }

  function toggleTag(key, type, tag) {
    setKeptTags((prev) => {
      const entry = prev[`${key}:${type}`];
      const tags = new Set(entry.tags);
      if (tags.has(tag)) tags.delete(tag);
      else tags.add(tag);
      return { ...prev, [`${key}:${type}`]: { ...entry, tags } };
    });
  }

  function grantRows(key, title, empty) {
    const grants = proposed?.[key] || [];
    return (
      <Box>
        <Label>{title}</Label>
        {grants.length ? (
          <Stack spacing={1}>
            {grants.map((g) => {
              const entry = keptTags[`${key}:${g.type}`];
              return (
                <Paper key={g.type} variant="outlined" sx={{ p: 1.25 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(entry?.type)}
                        onChange={() => toggleType(key, g.type)}
                      />
                    }
                    label={
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", flexWrap: "wrap" }}
                      >
                        <Typography sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                          {g.type}
                        </Typography>
                        {narrow[key].has(g.type) ? <UnusedMark what="unused in sandbox" /> : null}
                      </Stack>
                    }
                  />
                  {entry?.type && g.tags.length ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ flexWrap: "wrap", pl: 3.5 }}
                    >
                      {g.tags.map((t) => (
                        <FormControlLabel
                          key={t}
                          control={
                            <Checkbox
                              size="small"
                              checked={entry.tags.has(t)}
                              onChange={() => toggleTag(key, g.type, t)}
                            />
                          }
                          label={<Typography variant="body2">{t}</Typography>}
                        />
                      ))}
                    </Stack>
                  ) : null}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", pl: 3.5 }}
                  >
                    {entry?.type
                      ? g.permits
                      : "Struck. This app would read nothing of this type."}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {empty}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="approve-title"
    >
      <DialogTitle id="approve-title" sx={WRAPS}>
        {stage?.phase === "landed" ? "Approved " : stage?.phase === "waiting" ? "Approval recorded: " : "Approve "}
        <code>{request?.app_type}</code>
      </DialogTitle>
      {stage?.phase === "landed" ? (
        <>
          <DialogContent dividers sx={WRAPS}>{landed}</DialogContent>
          <DialogActions>
            <Button onClick={onCancel} autoFocus>Close</Button>
          </DialogActions>
        </>
      ) : stage?.phase === "waiting" ? (
        <RerunPanel
          key={`${stage.requestId}:${stage.decisionId}`}
          get={get}
          stage={stage}
          active={open}
          appType={request?.app_type}
          renderCeiling={(ceiling) => <Proposal ceiling={ceiling} />}
          onLanded={onLanded}
          onStopped={onStopped}
          onBackToLines={() => onStage(null)}
          onAsRequested={() => {
            tickEverything();
            onStage(null);
          }}
          onReject={onReject}
          onClose={onCancel}
        />
      ) : (
        <>
      <DialogContent dividers sx={WRAPS}>
        <Stack spacing={2}>
          <GateRefusal refusal={refusal} />

          <Alert severity="info" variant="outlined" icon={false}>
            <Typography variant="body2">
              Approving writes a manifest. From the next request, an app
              pointed at <code>{request?.app_type}</code> may read what is
              ticked below — bounded further, per person, by{" "}
              <code>manifest ∩ user</code>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              You can untick lines. You cannot add any: an approval wider than
              its request would be a grant nobody asked for, and the kernel
              refuses it.
            </Typography>
            {/* What striking does, in the mode the kernel says the gate is in.
                It is on the paragraph about unticking because that is the
                action it qualifies, and it is the kernel's word and not this
                screen's: a sentence kept here would be wrong the day the
                switch is flipped (LO-191). */}
            <Typography variant="body2" sx={{ mt: 0.5 }} data-gate-mode={words.mode}>
              <strong>{words.word}.</strong> {words.striking}
            </Typography>
            {words.problem ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: "kernel.attention" }}>
                {words.problem}.
              </Typography>
            ) : null}
          </Alert>

          {request?.replaces_version ? (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>
                This replaces version {request.replaces_version}
              </AlertTitle>
              <code>{request.app_type}</code> already has a ceiling this
              console approved. What you tick here becomes version{" "}
              {request.replaces_version + 1} in full — it is a replacement, not
              an addition, so anything not ticked is taken away.
            </Alert>
          ) : null}
          {/* What this version changes, against the manifest in force and the
              live build (LO-203), before a line is ticked or struck. */}
          {request?.replaces_version && current ? (
            <Box>
              <VersionChanges request={request} current={current} get={get} token={token} />
            </Box>
          ) : null}

          <UnusedBlock
            narrow={narrow}
            here={unusedHere}
            kept={unusedKept}
            hasSandbox={wantsSandbox(request)}
            onStrike={strikeUnused}
          />

          {grantRows(
            "reads",
            "Reads — with a user",
            "The request asks for no user-facing reads.",
          )}
          {grantRows(
            "service_reads",
            "Reads — with no user behind it",
            "The request asks for no service reads.",
          )}

          <Box>
            <Label>Writes</Label>
            {proposed?.actions?.length ? (
              <Stack spacing={0.25}>
                {proposed.actions.map((a) => (
                  <Box key={a.verb}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(keptActions[a.verb])}
                          onChange={() =>
                            setKeptActions((prev) => ({
                              ...prev,
                              [a.verb]: !prev[a.verb],
                            }))
                          }
                        />
                      }
                      label={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center", flexWrap: "wrap" }}
                        >
                          <Typography sx={{ fontFamily: "monospace" }}>
                            {a.control_plane ? `⚑ ${a.verb}` : a.verb}
                          </Typography>
                          {narrow.actions.has(a.verb) ? <UnusedMark what="unused in sandbox" /> : null}
                        </Stack>
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", pl: 3.5 }}
                    >
                      {a.detail}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                The request asks for no write actions.
              </Typography>
            )}
          </Box>

          {/* Both of these were listed as unused and offered no box, on the
              reading that approval could not trim them. It can: the approve
              route takes a replacement list for `service_actions` and for
              `aggregates` as it does for the rest (LO-191). Shown only when the
              request has some, so a request with none is not padded. */}
          {proposed?.service_actions?.length ? (
            <Box>
              <Label>Writes — with no user behind it</Label>
              <Stack spacing={0.25}>
                {proposed.service_actions.map((a) => (
                  <Box key={a.verb}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(keptServiceActions[a.verb])}
                          onChange={() =>
                            setKeptServiceActions((prev) => ({
                              ...prev,
                              [a.verb]: !prev[a.verb],
                            }))
                          }
                        />
                      }
                      label={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center", flexWrap: "wrap" }}
                        >
                          <Typography sx={{ fontFamily: "monospace" }}>
                            {a.control_plane ? `⚑ ${a.verb}` : a.verb}
                            {keptServiceActions[a.verb] ? "" : " (struck)"}
                          </Typography>
                          {narrow.service_actions.has(a.verb) ? <UnusedMark what="unused in sandbox" /> : null}
                        </Stack>
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", pl: 3.5 }}
                    >
                      {a.detail}
                    </Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 0.5 }}>
                  What its timer or its agent may do unattended, judged by this
                  list alone: there is no person to intersect with.
                </Typography>
              </Stack>
            </Box>
          ) : null}

          {proposed?.aggregates?.length ? (
            <Box>
              <Label>Aggregates — figures over records a person could not open</Label>
              <Stack spacing={0.25}>
                {proposed.aggregates.map((a) => (
                  <Box key={a.name}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(keptAggregates[a.name])}
                          onChange={() =>
                            setKeptAggregates((prev) => ({
                              ...prev,
                              [a.name]: !prev[a.name],
                            }))
                          }
                        />
                      }
                      label={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center", flexWrap: "wrap" }}
                        >
                          <Typography sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                            {a.name}
                            {keptAggregates[a.name] ? "" : " (struck)"}
                          </Typography>
                          {narrow.aggregates.has(a.name) ? <UnusedMark what="unused in sandbox" /> : null}
                        </Stack>
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", pl: 3.5, overflowWrap: "anywhere" }}
                    >
                      {describeAggregate(a)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}

          <Box>
            <Label>Agents</Label>
            {proposed?.agents?.length ? (
              <Stack spacing={0.25}>
                {proposed.agents.map((agent) => (
                  <Box key={agent.name}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(keptAgents[agent.name])}
                          onChange={() =>
                            setKeptAgents((prev) => ({
                              ...prev,
                              [agent.name]: !prev[agent.name],
                            }))
                          }
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: "monospace" }}>
                          {agent.name}
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", pl: 3.5, overflowWrap: "anywhere" }}
                    >
                      {agent.division} — {agent.does}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                The request declares no agents.
              </Typography>
            )}
          </Box>

          <Box>
            <Label>Egress — hosts the app may call</Label>
            {proposed?.egress?.length ? (
              <Stack spacing={0.25}>
                {proposed.egress.map((host) => (
                  <Box key={host}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(keptEgress[host])}
                          onChange={() =>
                            setKeptEgress((prev) => ({
                              ...prev,
                              [host]: !prev[host],
                            }))
                          }
                        />
                      }
                      label={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center", flexWrap: "wrap" }}
                        >
                          <Typography
                            sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                          >
                            {host}
                            {keptEgress[host] ? "" : " (struck)"}
                          </Typography>
                          {narrow.egress.has(host) ? (
                            <UnusedMark what="never contacted in sandbox" />
                          ) : null}
                        </Stack>
                      }
                    />
                  </Box>
                ))}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", pl: 0.5 }}
                >
                  Unticking a host strikes it: the app is approved without it,
                  and so is a real install. You cannot add one. {wantsSandbox(request)
                    ? "The hosts the sandbox saw it call are on the card, beside this list."
                    : "This request has no sandbox, so no host was observed and this is the declared list alone."}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                The request asks for no egress hosts.
              </Typography>
            )}
          </Box>

          {struck ? (
            <Alert severity="info" variant="outlined">
              {struck === 1 ? "One line struck" : `${struck} lines struck`}{" "}
              from what was asked for. The request stays in the queue marked
              approved, showing both what was proposed and what you granted.
              {/* What pressing Approve will do with this, in the kernel's mode:
                  attached to the button it is about, and said only once
                  something is struck because only then does the mode change
                  what the button does (LO-191). */}{" "}
              {words.approving}
            </Alert>
          ) : null}

          {orphans.length ? (
            // A button the kernel is certain to refuse is a worse answer than the
            // sentence that says why it is held.
            <Alert severity="warning" variant="outlined">
              <AlertTitle>
                {orphans.length === 1 ? "An aggregate is left with nothing to compute over" : "Aggregates are left with nothing to compute over"}
              </AlertTitle>
              {orphans.map((a, i) => (
                <span key={a.name}>
                  {i ? ", " : ""}
                  <code>{a.name}</code>
                </span>
              ))}{" "}
              {orphans.length === 1 ? "computes" : "compute"} over a type no longer ticked
              under <em>Reads — with a user</em>. An aggregate may reach past the person
              and never past the app, so the kernel refuses one over a type the app may
              not read. Strike {orphans.length === 1 ? "it" : "them"} too, or tick the read
              back.
            </Alert>
          ) : null}

          {nothing ? (
            // Permitted, and worth saying out loud. "No manifest" and "a
            // manifest declaring nothing" are different answers in the
            // kernel, and only the second is one somebody chose.
            <Alert severity="warning" variant="outlined">
              <AlertTitle>This would grant nothing at all</AlertTitle>
              <code>{request?.app_type}</code> would get a manifest that
              declares no reads and no writes. That is different from
              rejecting: the app becomes known and permitted nothing, rather
              than unknown. If you meant no, reject it with a reason instead.
            </Alert>
          ) : null}

          <Box>
            <Typography variant="overline" sx={{ display: "block" }}>
              Where it will live
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Name"
              value={displayName}
              disabled={busy}
              onChange={(e) => setDisplayName(e.target.value)}
              sx={{ mt: 1, maxWidth: "32rem" }}
              slotProps={{ htmlInput: { maxLength: 60 } }}
            />
            <Typography
              variant="caption"
              color={slug.length >= 3 ? "text.secondary" : "error.main"}
              sx={{ display: "block", mt: 0.5 }}
            >
              {slug.length >= 3
                ? `It will answer on ${slug}.apps.lopie.one. Fixed once you `
                  + `approve — changing it later means installing again under `
                  + `a second name. The name is recorded with your approval, and `
                  + `the platform releases and installs the app under it when the `
                  + `approval lands, whether or not this window is open.`
                : "Three letters or more, starting with a letter."}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          ref={approveButton}
          variant="contained"
          disabled={busy || slug.length < 3 || orphans.length > 0}
          onClick={() => onConfirm(trimmed, displayName.trim())}
        >
          {busy
            ? "Approving…"
            // The word on the button is what it will do: with the gate enforced
            // and a line struck it records the decision and waits (LO-191).
            : words.mode === "enforce" && struck
              ? `Approve ${request?.app_type || ""} after re-test`
              : `Approve ${request?.app_type || ""}`}
        </Button>
      </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/**
 * Approve an object type or a sync mapping: read-only, one button, because
 * neither trims. What is shown is exactly what `ObjectTypeBody`/`SyncMapBody`
 * render on the card, so a reviewer sees the same document twice rather than
 * a summary of it once.
 */
function SimpleApproveDialog({ kind, request, open, busy, onCancel, onConfirm }) {
  const title = request ? titleFor(kind, request) : null;
  const intro = NON_MANIFEST_INTRO[kind];
  const Body = NON_MANIFEST_BODY[kind];
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="simple-approve-title"
    >
      <DialogTitle id="simple-approve-title" sx={WRAPS}>
        Approve {title}
      </DialogTitle>
      <DialogContent dividers sx={WRAPS}>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined" icon={false}>
            <Typography variant="body2">{intro}</Typography>
          </Alert>
          {request && Body ? <Body request={request} /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy} onClick={onConfirm}>
          {busy ? "Approving…" : "Approve"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Reject, with a reason that is not optional. Shared by all three kinds —
 * `title` says which request is being rejected, and defaults to the manifest
 * reading so the existing call site needs no change. */
function RejectDialog({ request, open, busy, onCancel, onConfirm, title }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    setReason("");
  }, [request]);

  const heading = title ?? <code>{request?.app_type}</code>;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="reject-title"
    >
      <DialogTitle id="reject-title" sx={WRAPS}>
        Reject {heading}
      </DialogTitle>
      <DialogContent dividers sx={WRAPS}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Whoever asked can read this. A rejection with no reason is a
            request that arrives again next week in the same shape, which is
            why the reason is required rather than encouraged.
          </Typography>
          <TextField
            label="Why not"
            multiline
            minRows={3}
            fullWidth
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Payments was not justified by the stated purpose. Ask again without it."
          />
          <Typography variant="body2" color="text.secondary">
            Nothing is granted and nothing is deleted. The request stays in the
            queue as rejected, with this reason and your name on it.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="inherit"
          disabled={busy || !reason.trim()}
          onClick={() => onConfirm(reason.trim())}
        >
          {busy ? "Rejecting…" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Who({ request }) {
  const origin = requestOrigin(request);
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" color="text.secondary">
        {origin.unattended ? (
          <>
            Proposed automatically by{" "}
            <code>{request.requested_by_app || "an app"}</code> — no person
            is behind this request
          </>
        ) : (
          <>
            Asked by <code>{origin.actor || "unknown"}</code> through{" "}
            <code>{request.requested_by_app || "an app"}</code>
          </>
        )}
        {request.requested_at ? ` at ${request.requested_at}` : null}.
      </Typography>
      {origin.unattended && request.requested_by_instance ? (
        <Typography variant="caption" color="text.secondary">
          Its running instance: <code>{request.requested_by_instance}</code>.
        </Typography>
      ) : null}
      {request.decided_at ? (
        <Typography variant="body2" color="text.secondary">
          {CLOSED_WORD[request.state] || request.state} by{" "}
          <code>{request.decided_by || "unknown"}</code> at{" "}
          {request.decided_at}.
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * An approval that landed with nobody reading it, and what that left undone (LO-196).
 *
 * The kernel writes the manifest and does not release a build or start an install.
 * The console does both, but only while the approve window is open at the moment the
 * approval lands, because the name the app is installed under (its address, fixed
 * once chosen) is typed in that window and recorded nowhere else. So a decision the
 * kernel landed by itself with the window closed is approved and not installed, and
 * the request says so (`follow_through`). This is where the card says so as well,
 * and offers the one action that finishes it.
 *
 * What the request says is what the landing did, once. Whether the build has been
 * released and an install made since is read from the build's own record and the
 * control plane's list of installs (`followThroughLeft`), and the notice is shown
 * only while something is left: an approval that has been finished, or never needed
 * finishing, shows nothing. A read that did not answer is said as that and is never
 * taken for either answer.
 */
function FollowThroughNotice({ request, check, busy, onFinish }) {
  const through = openFollowThrough(request);
  // Not read yet: say nothing for the length of a round trip, as the sandbox chip does.
  if (!through || check === undefined) return null;
  const left = followThroughLeft({ through, build: check.build, instances: check.instances });
  if (left.done) return null;

  let title = "Approved and installed, and this build is not released";
  if (left.unknown) {
    title = "Approved. Whether it was released and installed could not be checked";
  } else if (left.released === false && left.installed === false) {
    title = "Approved, and not released and not installed";
  } else if (left.installed === false) {
    title = "Approved and released, and not installed";
  }
  const said = (value, yes, no) => (value === null ? "not read" : value ? yes : no);
  return (
    <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
      <AlertTitle>{title}</AlertTitle>
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {through.detail}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          Landed by <code>{through.landed_by || "the kernel"}</code>
          {through.landed_at ? ` at ${through.landed_at}` : ""}. Checked just now
          against the control plane and the build&rsquo;s own record: build{" "}
          <code>{through.build_id}</code> is {said(left.released, "released", "not released")};
          an install of <code>{through.app_type}</code> is{" "}
          {said(left.installed, "there", "not there")}.
          {left.unknown
            ? " Release and install is safe to press either way: a build is released once and an app installed once, and the result says what it found."
            : ""}
        </Typography>
        <Box>
          <Button variant="contained" disabled={busy} onClick={() => onFinish(request)}>
            Release and install…
          </Button>
        </Box>
      </Stack>
    </Alert>
  );
}

/**
 * An approval the platform is releasing and installing by itself (LO-202).
 *
 * The name was given at approval and recorded on the decision, so nobody presses
 * anything: the provisioner releases the build and installs the app. This shows how far
 * it has got, from the control plane's follow-through record, and offers Release and
 * install only when the follow-through failed in a way it will not retry (a name that is
 * taken, most often). A record that could not be read is said as that.
 */
function FollowingNotice({ request, record, busy, onFinish }) {
  const through = startedFollowThrough(request);
  const said = followThroughSays(through, record);
  if (!said) return null;
  return (
    <Alert severity={said.severity} variant="outlined" sx={{ mt: 1.5 }}>
      <AlertTitle sx={{ overflowWrap: "anywhere" }}>{said.title}</AlertTitle>
      <Stack spacing={1}>
        {said.detail ? (
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {said.detail}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          Name given at approval: <code>{through.install?.name}</code>. Read from the
          control plane&rsquo;s record of this approval just now; the kernel itself
          cannot see the control plane.
        </Typography>
        {said.offerFinish ? (
          <Box>
            <Button variant="contained" disabled={busy} onClick={() => onFinish(request)}>
              Release and install…
            </Button>
          </Box>
        ) : null}
      </Stack>
    </Alert>
  );
}

/**
 * The one action that finishes an approval its landing left undone (LO-196): release
 * the build the request named, then install the app, under a name typed here.
 *
 * The name is what the approve window asks for too, with the same suggestion and the
 * same rule, because it is the one thing the kernel could not choose: it is the
 * address the app answers on and cannot be changed afterwards. Nothing else is asked.
 * The approval is not touched, so there is no ceiling here and nothing to strike.
 */
function FinishDialog({ request, open, busy, onCancel, onConfirm }) {
  const [displayName, setDisplayName] = useState("");
  useEffect(() => {
    if (request?.app_type) setDisplayName(titleOf(request.app_type));
  }, [request?.app_type]);
  const slug = slugOf(displayName);
  const through = request?.follow_through;
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="finish-title"
    >
      <DialogTitle id="finish-title" sx={WRAPS}>
        Release and install <code>{request?.app_type}</code>
      </DialogTitle>
      <DialogContent dividers sx={WRAPS}>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined" icon={false}>
            <Typography variant="body2">
              The approval is already written and this does not change it: the
              manifest says what <code>{whoIs(request?.decided_by) || "the approver"}</code>{" "}
              decided. This does the two things its landing did not. It releases
              build <code>{through?.build_id || request?.proposed?.build_id}</code>,
              then installs the app. A build that is already released is not
              released again, and an app that is already installed is not
              installed a second time; the result says what was found.
            </Typography>
          </Alert>
          <Box>
            <Typography variant="overline" sx={{ display: "block" }}>
              Where it will live
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Name"
              value={displayName}
              disabled={busy}
              onChange={(e) => setDisplayName(e.target.value)}
              sx={{ mt: 1, maxWidth: "32rem" }}
              slotProps={{ htmlInput: { maxLength: 60 } }}
            />
            <Typography
              variant="caption"
              color={slug.length >= 3 ? "text.secondary" : "error.main"}
              sx={{ display: "block", mt: 0.5 }}
            >
              {slug.length >= 3
                ? `It will answer on ${slug}.apps.lopie.one. Fixed once you `
                  + "install, so changing it later means installing again under "
                  + "a second name. Not used if the app is already installed."
                : "Three letters or more, starting with a letter."}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || slug.length < 3}
          onClick={() => onConfirm(displayName.trim())}
        >
          {busy ? "Releasing and installing…" : "Release and install"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The shell every kind of request shares: a heading, its state and kind,
 * who asked and who decided, the rejection reason if there is one, then
 * whatever body the caller hands it, then the decide buttons. Nothing here
 * knows what a manifest, a type or a mapping actually is — that lives in
 * `ManifestBody`/`ObjectTypeBody`/`SyncMapBody`, passed in as `children`.
 */
function RequestCard({
  request, kind, title, extraChips, notice, onApprove, onFollow, onReject, busy, mayDecide,
  whyNot, children,
}) {
  const pending = request.state === "pending";
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, ...WRAPS }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Typography
          variant="h2"
          sx={{ fontFamily: "monospace", overflowWrap: "anywhere", minWidth: 0 }}
        >
          {title}
        </Typography>
        <StateChip state={request.state} />
        {requestOrigin(request).unattended ? <UnattendedChip /> : null}
        <Chip size="small" variant="outlined" label={KIND_META[kind].label} />
        {extraChips}
      </Stack>

      <Who request={request} />

      {request.state === "rejected" ? (
        <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
          <AlertTitle>Rejected</AlertTitle>
          {request.reason}
        </Alert>
      ) : null}
      {request.state === "superseded" ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
          <AlertTitle>Replaced, so it can no longer be decided</AlertTitle>
          A newer request for this app
          {request.superseded_by ? <> (<code>{request.superseded_by}</code>)</> : null}{" "}
          took its place. Decide that one.
        </Alert>
      ) : null}
      {request.state === "cancelled" ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
          <AlertTitle>Cancelled by whoever asked</AlertTitle>
          {request.reason || "No reason given."} Nothing was granted or removed.
        </Alert>
      ) : null}

      {notice}

      {children}

      {pending && mayDecide ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => onApprove(request)}
          >
            Approve…
          </Button>
          {/* A governor's narrowed decision is recorded and waits for its re-run.
              Closing the window that was waiting does not cancel it, and this is
              the way back to it. The kernel lands it by itself once the run has
              passed (LO-196), so this is no longer what lands it: it reopens the
              window, which shows the run and, when the approval has landed with the
              window open, releases the build and installs the app (LO-191). */}
          {kind === "manifest" && request.approval_pending ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => onFollow(request)}
            >
              Follow the re-run…
            </Button>
          ) : null}
          <Button
            variant="outlined"
            color="inherit"
            disabled={busy}
            onClick={() => onReject(request)}
          >
            Reject…
          </Button>
        </Stack>
      ) : pending ? (
        // Not a disabled button. A control that looks like it should work and
        // does not is a worse answer than the sentence explaining why it is
        // not there, and the sentence is the same one the kernel would return
        // if the click were made anyway.
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          <AlertTitle>Waiting on a governor, not on you</AlertTitle>
          {whyNot}
        </Alert>
      ) : null}
    </Paper>
  );
}

/** The endpoint each kind's queue is read from and decided against. */
const ENDPOINT = {
  manifest: "/manifest-requests",
  object_type: "/object-type-requests",
  syncmap: "/connector-map-requests",
  map_override: "/map-override-requests",
};

const KINDS = ["manifest", "object_type", "syncmap", "map_override"];

/**
 * Why a pending request is waiting on somebody else, for one kind. The two
 * gates are the same shape for all three — the app declares the action (or
 * this card would not exist to read) and the person is on the governor list
 * — so this is the one paragraph parameterised by which action's name it
 * names, rather than three near-copies that could drift apart.
 */
function buildWhyNot(actionNode, governance) {
  if (governance.configured) {
    return (
      <>
        This console declares {actionNode} — that half passes — and you are
        not a governor of this kernel, which is a different decision by a
        different document.{" "}
        {governance.count === 1 ? "The governor is " : "Governors are "}
        {governance.governors.map((g, i) => (
          <span key={g}>
            {i ? ", " : ""}
            <code>{g}</code>
          </span>
        ))}
        . The list is committed in <code>governors/</code> and applied out of
        band, so the way in is to ask one of them or to change the file — not
        to widen a manifest, which would not help.
      </>
    );
  }
  return (
    <>
      {governance.present
        ? "A governor list has been applied to this kernel and it names nobody."
        : "No governor list has been applied to this kernel."}{" "}
      Deciding needs two things — this console declares {actionNode}, which it
      does, and the person deciding is a governor. Until a list exists there
      is no such person, so approve and reject are refused for everybody
      including you. That is a deployment state rather than a permission, and
      it is repaired by running <code>governors/apply.py --write</code>{" "}
      against this kernel; there is deliberately no button that does it.
    </>
  );
}

/** A kind's queue could not be read at all — most often because this
 * console's own manifest does not declare the action that kind needs, which
 * is information rather than a screen-wide failure (LO-126, step 3). */
function KindNotice({ label, failure, onRetry }) {
  if (!failure) return null;
  return (
    <Alert severity={failure.isManifest ? "info" : "error"} variant="outlined">
      <AlertTitle>{label} requests are not shown here</AlertTitle>
      {failure.isManifest ? (
        <>
          This console does not declare the action {label} decisions need, so
          the kernel refuses before any row is read.
        </>
      ) : (
        <>{failure.message}</>
      )}{" "}
      <Button size="small" onClick={onRetry}>
        Retry
      </Button>
    </Alert>
  );
}

/** The title and, for a decided one, the reason/outcome — read from a
 * generic (`object_type`/`syncmap`) response so the two kinds share one
 * outcome renderer rather than each growing its own. */
function GenericOutcomeBody({ body }) {
  return (
    <Stack spacing={0.5}>
      {body.provenance ? (
        <Typography variant="body2">
          Recorded as <code>{body.provenance.action}</code> by{" "}
          <code>{body.provenance.on_behalf_of || "nobody"}</code> through{" "}
          <code>{body.provenance.actor}</code>, mode{" "}
          <code>{body.provenance.mode}</code>.
        </Typography>
      ) : null}
      {body.reason ? (
        <Typography variant="body2">Reason: {body.reason}</Typography>
      ) : null}
      {(body.does_not_prove || []).map((line) => (
        <Typography variant="body2" color="text.secondary" key={line}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

function outcomeTitle(outcome) {
  if (!outcome.ok) return "The decision did not go through";
  const b = outcome.body;
  if (outcome.kind === "manifest") {
    if (outcome.finished) return `Finished ${b.app_type}: what its approval left undone`;
    return b.state === "approved"
      ? `Approved ${b.app_type} — version ${b.version}`
      : `Rejected ${b.app_type}`;
  }
  const decl = b.request?.declaration || {};
  if (outcome.kind === "object_type") {
    const name = decl.name || b.type || "";
    return b.state === "approved" ? `Approved type ${name}` : `Rejected type ${name}`;
  }
  if (outcome.kind === "map_override") {
    const label = decl.source && decl.concept
      ? `${decl.source}/${decl.concept}`
      : b.source && b.concept
        ? `${b.source}/${b.concept}`
        : "";
    return b.state === "approved"
      ? `Approved override ${label}`
      : `Rejected override ${label}`;
  }
  const label = decl.source && decl.table
    ? `${decl.source}/${decl.table}`
    : b.source && b.table
      ? `${b.source}/${b.table}`
      : "";
  return b.state === "approved" ? `Approved mapping ${label}` : `Rejected mapping ${label}`;
}

/**
 * What a decision did, said once and shown in two places: at the top of the
 * screen, where it stays until it is closed, and in the approve dialog when the
 * approval was the kind that waited for a sandbox re-run and landed while the
 * dialog was open (LO-191).
 *
 * Two shapes of body reach it. The write's own answer carries `provenance`. An
 * approval that landed on a later read carries none, because the read returns the
 * request and not the write's receipt; `outcome.landed` says so and the sentence
 * is built from what the read did return.
 */
function OutcomeAlert({ outcome, onClose }) {
  return (
    <Alert
      severity={outcome.ok ? "success" : "error"}
      variant="outlined"
      onClose={onClose}
    >
      <AlertTitle>{outcomeTitle(outcome)}</AlertTitle>
      {outcome.ok ? (
        outcome.kind === "manifest" ? (
          <Stack spacing={0.5}>
            {outcome.body.provenance ? (
              <Typography variant="body2">
                Recorded as <code>{outcome.body.provenance.action}</code> by{" "}
                <code>{outcome.body.provenance.on_behalf_of || "nobody"}</code>{" "}
                through <code>{outcome.body.provenance.actor}</code>, mode{" "}
                <code>{outcome.body.provenance.mode}</code>.
              </Typography>
            ) : outcome.landed ? (
              // An approval that waited for a sandbox re-run has no `provenance`: the
              // 202 wrote nothing, and the write happened on a later read of the
              // request. What the read returns is the request, so that is what is said.
              <Typography variant="body2">
                Recorded as <code>manifest.approve</code> by{" "}
                <code>{whoIs(outcome.landed.progress.by) || "nobody"}</code>
                {outcome.landed.progress.at ? ` at ${outcome.landed.progress.at}` : ""}.{" "}
                {outcome.landed.progress.viaThisRead
                  ? "It landed after the re-run: the sandbox passed the narrowed manifest, and the decision recorded for it was written when this screen read the request."
                  : "It landed while this screen was waiting: the kernel's own sweep, another read of the request or a decision made elsewhere wrote it, as the approver decided it; what was granted is on the card."}
              </Typography>
            ) : outcome.finished ? (
              <Typography variant="body2">
                The approval was already written, as{" "}
                <code>{whoIs(outcome.finished.decidedBy) || "nobody"}</code>{" "}
                decided it, and is not changed by this. This did the release and
                the install that its landing did not.
              </Typography>
            ) : null}
            {outcome.following ? (
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                The platform {outcome.following.build_id
                  ? <>releases build <code>{outcome.following.build_id}</code> and </>
                  : ""}
                installs it as <code>{outcome.following.install?.name}</code>, the
                name given here, with nobody pressing anything. How far it has got is
                on the request&rsquo;s card.
              </Typography>
            ) : null}
            {/* The build, when the approval caused one — before the
                install, because it is what makes the install possible: a
                catalog row does not exist until this succeeds. Said out
                loud for the same reason the install is, below. */}
            {outcome.published && !outcome.published.failed ? (
              <Typography variant="body2">
                Build <code>{outcome.published.buildId}</code> is released
                as version <code>{outcome.published.version}</code>.
                Approving an app the assistant built releases the exact
                build it named — the decision was this one.
              </Typography>
            ) : null}
            {outcome.published?.failed ? (
              <Typography variant="body2" sx={{ color: "kernel.attention" }}>
                Approved, and build{" "}
                <code>{outcome.published.buildId}</code> was not released:{" "}
                {outcome.published.failed} The manifest is written and that
                half stands; nothing is installed until a release exists.
              </Typography>
            ) : null}
            {/* The install, when the approval caused one. Said out loud
                rather than left to the Portal, because provisioning is a
                second thing that happened and somebody who approved a
                ceiling should not find out later that it also built
                infrastructure. */}
            {outcome.installed?.existing ? (
              <Typography variant="body2">
                Already installed as <code>{outcome.installed.existing}</code>,
                so nothing new was installed.{" "}
                {outcome.installed.autoUpdate
                  ? "It has auto-update on and moves to this version by itself."
                  : "Auto-update is off, so it stays on its current version until you press Update on the Apps screen."}
              </Typography>
            ) : null}
            {outcome.installed && !outcome.installed.failed && !outcome.installed.existing ? (
              <Typography variant="body2">
                It is in the library and is being provisioned as{" "}
                <code>{outcome.installed}</code>. Approving an app the
                library can build installs it — the decision was this one,
                and a separate Install button would be the same decision
                asked twice.
              </Typography>
            ) : null}
            {outcome.installed?.failed ? (
              <Typography variant="body2" sx={{ color: "kernel.attention" }}>
                Approved, and the install did not start:{" "}
                {outcome.installed.failed}. The manifest is written and that
                half stands — this is the provisioning half, and it can be
                retried from the Portal.
              </Typography>
            ) : null}
            {outcome.body.grants_nothing ? (
              <Typography variant="body2">
                Everything was struck, so this app type now has a manifest
                that declares nothing. It is known and permitted nothing,
                which is not the same as unknown.
              </Typography>
            ) : null}
            {(outcome.body.does_not_prove || []).map((line) => (
              <Typography variant="body2" color="text.secondary" key={line}>
                {line}
              </Typography>
            ))}
          </Stack>
        ) : (
          <GenericOutcomeBody body={outcome.body} />
        )
      ) : (
        <Stack spacing={0.5}>
          <Typography variant="body2">{outcome.error.message}</Typography>
          {(outcome.error.body?.widenings || []).map((line) => (
            <Typography variant="body2" key={line}>
              {line}
            </Typography>
          ))}
          {outcome.error.error === "audit_not_written" ? (
            <Typography variant="body2">
              The ceiling is in force and the log does not say who granted
              it. The log is the thing to fix.
            </Typography>
          ) : null}
        </Stack>
      )}
    </Alert>
  );
}


export default function Requests({ get, post, token }) {
  const [data, setData] = useState({
    manifest: null, object_type: null, syncmap: null, map_override: null,
  });
  const [kindFailure, setKindFailure] = useState({
    manifest: null, object_type: null, syncmap: null, map_override: null,
  });
  const [loaded, setLoaded] = useState(false);
  const [approving, setApproving] = useState(null); // { kind, request }
  // What the approve dialog is showing besides its form (LO-191). `refusal` is the
  // gate's 409, shown in the dialog above the lines the approver left as they were
  // so they can try again. `stage` is `null` (the form), `{phase: "waiting", ...}`
  // (a narrowed decision was recorded and its re-run is being read) or
  // `{phase: "landed"}` (it passed, and the approval was written).
  const [refusal, setRefusal] = useState(null);
  const [stage, setStage] = useState(null);
  const [rejecting, setRejecting] = useState(null); // { kind, request }
  // An approval that landed with nobody reading it, being finished (LO-196), and what
  // the control plane says of each such approval, by request id. `undefined` is not
  // asked yet; `{ build, instances }` is the answer, each `null` when it did not come.
  const [finishing, setFinishing] = useState(null);
  const [followCheck, setFollowCheck] = useState({});
  // The control plane's follow-through record for each approval the platform is
  // releasing and installing (LO-202), by request id: `undefined` not asked,
  // `{missing}` nothing recorded yet, `{unreadable}` no answer, or the record.
  const [followRecord, setFollowRecord] = useState({});
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  // Which decided requests to show. Pending is never filtered — it is the
  // one list a governor opens this screen to act on, and hiding part of it
  // behind a control would be the one place a filter could cost something.
  // "Already decided" is history, and history is what a filter is for.
  const [decidedFilter, setDecidedFilter] = useState("all");
  // Today's ceilings, by app type, so a request replacing one can say what
  // it changes rather than only what it asks for.
  const [currentByApp, setCurrentByApp] = useState({});
  // What the control plane says of each open request's sandbox, by request id
  // (LO-190). `undefined` is not asked yet (and, for a closed request, never
  // asked), `null` is the control plane saying there is none, `{ unreadable }`
  // is it not answering, and a body is the answer. The kernel's own view of the
  // sandbox rides on the request, so a screen with only half still says that
  // half instead of nothing.
  const [sandboxControl, setSandboxControl] = useState({});

  const load = useCallback(async () => {
    const results = await Promise.all(
      KINDS.map(async (kind) => {
        try {
          return [kind, await get(ENDPOINT[kind]), null];
        } catch (e) {
          return [kind, null, e];
        }
      }),
    );
    const nextData = {};
    const nextFailure = {};
    for (const [kind, body, err] of results) {
      nextData[kind] = body;
      nextFailure[kind] = err;
    }
    setData(nextData);
    setKindFailure(nextFailure);
    try {
      const apps = await get("/apps");
      setCurrentByApp(Object.fromEntries(
        (apps?.apps || []).map((a) => [a.app_type, a])));
    } catch {
      setCurrentByApp({});
    }
    setLoaded(true);
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  // Each open manifest request that names a build may have a sandbox, and the
  // control plane is the only thing that knows whether it is up (LO-190).
  // Asked per request because that is how it is keyed, and only for the ones
  // `wantsSandbox` names: a sandbox exists only while its request is a draft
  // or pending, so a closed request is never asked about (the answer is a 404
  // the browser prints as a console error on every visit, and cannot change
  // what the card says). A 404 is the control plane saying there is no sandbox
  // and leaves `null`; anything else is kept as `unreadable` (`controlFailure`)
  // so the card says the control plane did not answer, rather than nothing.
  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    const wanted = (data.manifest?.requests || []).filter(wantsSandbox);
    for (const r of wanted) {
      controlGet(token)(`/sandboxes/${r.request_id}`)
        .then((body) => live && setSandboxControl((m) => ({ ...m, [r.request_id]: body })))
        .catch((e) => live && setSandboxControl((m) => ({ ...m, [r.request_id]: controlFailure(e) })));
    }
    return () => {
      live = false;
    };
  }, [token, data.manifest]);

  // An approval the kernel landed by itself (LO-196) is approved, and was not
  // released or installed by its landing. The request says so once, when it landed;
  // whether anyone has released the build or installed the app since is a fact of the
  // build's own record and the control plane, and is read here for each such request
  // so the card says what is left and not what was true at the landing. Nothing is
  // asked for any other request, so the history costs no round trips.
  useEffect(() => {
    if (!token) return undefined;
    const wanted = (data.manifest?.requests || []).filter((r) => openFollowThrough(r));
    if (!wanted.length) return undefined;
    let live = true;
    (async () => {
      let instances = null;
      try {
        instances = (await controlGet(token)("/instances"))?.instances || [];
      } catch {
        instances = null;
      }
      for (const r of wanted) {
        let build = null;
        try {
          build = await get(`/builds/${r.app_type}/${r.proposed?.build_id}`);
        } catch {
          build = null;
        }
        if (live) setFollowCheck((m) => ({ ...m, [r.request_id]: { build, instances } }));
      }
    })();
    return () => {
      live = false;
    };
  }, [token, get, data.manifest]);

  // Approvals the platform is following through (LO-202): read what it has done,
  // and again every fifteen seconds while one is not finished, so the card shows the
  // install coming up without a reload. Nothing is asked for any other request.
  useEffect(() => {
    if (!token) return undefined;
    const wanted = (data.manifest?.requests || []).filter((r) => startedFollowThrough(r));
    if (!wanted.length) return undefined;
    let live = true;
    let timer = null;
    const read = async () => {
      let pending = false;
      for (const r of wanted) {
        let record;
        try {
          record = await controlGet(token)(`/follow-through/${r.request_id}`);
        } catch (e) {
          record = e?.status === 404 || e?.body?.error === "no_follow_through"
            ? { missing: true }
            : { unreadable: e?.body?.message || e?.message || "no answer" };
        }
        const said = followThroughSays(startedFollowThrough(r), record);
        if (!said?.done && said?.phase !== "failed") pending = true;
        if (live) setFollowRecord((m) => ({ ...m, [r.request_id]: record }));
      }
      if (live && pending) timer = setTimeout(read, 15000);
    };
    read();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [token, data.manifest]);

  // The QA report, call log and egress log of each of those (LO-185). Read
  // here and not inside the card because the approve dialog needs the same
  // unused lines the card shows, and two reads of one log could disagree.
  const { evidence, reload } = useSandboxEvidence(get, data.manifest?.requests);

  /**
   * Approving an app that the library can build also builds it.
   *
   * ## Why there is no second button
   *
   * The Library is a catalogue of templates somebody browses and installs.
   * An app requested through the build assistant is not that: it was
   * declared, and its ceiling was just reviewed and approved on this screen.
   * Asking for a separate **Install** afterwards is a second decision about
   * something already decided, and the person making it is the same person
   * who just approved the thing.
   *
   * ## Why it is here and not in the kernel
   *
   * The kernel approves; the control plane provisions; they are separate
   * services in separate accounts, and the kernel is deliberately ignorant of
   * deployment — `provisioner/handler.py` argues that the separation is what
   * makes its pointer write legitimate, because the writer is not the app it
   * writes about. A kernel that called the control plane on approve would be
   * teaching it about Crossplane to save a click.
   *
   * This console already holds both `manifest.review` and
   * `install.configure`. One person, one action, two writes — and each is
   * still refused independently by the service that owns it.
   *
   * ## It is best effort, and says so
   *
   * The approval is the governance act and it has already succeeded. An
   * install that fails afterwards must not read as an approval that failed,
   * so it is reported separately and never rethrown. An app with no catalog
   * row is skipped silently: most approvals are not installable apps.
   *
   * Manifest-only: object types and sync mappings never provision anything.
   */
  async function catalogEntry(appType) {
    if (!token) return null;
    try {
      const catalog = await controlGet(token)("/catalog");
      return (catalog?.apps || catalog?.catalog || []).find(
        (a) => a.app === appType,
      ) || null;
    } catch {
      return null;
    }
  }

  /**
   * Releasing the build the approved request named, so the catalog row
   * `provision` needs exists by the time it runs (LO-128).
   *
   * Seconds, not minutes: the build already ran, and succeeded, before the
   * request reached this queue. This copies its outputs, applies its config
   * and writes the row. Nothing on this screen waits for a build.
   *
   * Only reached for a request that named a `build_id` — a committed-file
   * app, or one whose release arrived some other way, skips straight to
   * `provision`, unchanged. Best effort and reported separately, the same
   * shape `provision` is: the approval has already been written, and a
   * release that is refused must not read as the approval having failed.
   */
  async function publishBuild(appType, buildId) {
    try {
      const released = await post(`/publisher/${appType}/releases`, {
        build_id: buildId,
      });
      return { buildId, version: released.version };
    } catch (e) {
      return {
        buildId,
        failed: e?.body?.detail || e?.message || "the publisher refused",
      };
    }
  }

  async function provision(appType, displayName, requestId) {
    if (!token) return null;
    try {
      const entry = await catalogEntry(appType);
      if (!entry) return null;
      // Already installed: this approval released a new version of an app
      // that is running, and a second install would be a second copy under a
      // second name (LO-128). The existing instance moves by itself if
      // auto-update is on, or with Update on the Apps screen if it is not.
      const listed = await controlGet(token)("/instances");
      // Never a sandbox of this app: that is a test of it, and counting it as
      // the install would skip installing the real one (LO-190).
      const running = withoutSandboxes(listed?.instances).find(
        (i) => i.app === appType && i.desiredState !== "absent",
      );
      if (running) {
        return { existing: running.name, autoUpdate: Boolean(running.autoUpdate) };
      }
      const created = await controlPost(token)("/instances", {
        app: appType,
        // What the approver typed, falling back to the catalog's own name.
        // The fallback matters: the field is only offered on this dialog, and
        // an approval arriving from anywhere else should still install.
        displayName: displayName || entry.name || appType,
        packageVersion: entry.defaultVersion || entry.defaultRelease,
        // The approval this install follows (LO-202): the control plane installs
        // one approval once, so this and the platform's own follow-through cannot
        // make two installs under two names.
        ...(requestId ? { requestId } : {}),
      });
      return created?.instance?.name || created?.name || appType;
    } catch (e) {
      if (e?.body?.error === "request_already_installed") {
        return { existing: e.body.name, autoUpdate: false };
      }
      return { failed: e?.body?.detail || e?.body?.message || e?.message || "the control plane refused" };
    }
  }

  /**
   * What an approval is followed by, once it is written: release the build the
   * request named, then install it. Shared by an approval that landed at once and
   * one that waited for its re-run, so the two cannot drift apart.
   */
  async function followOn(request, displayName) {
    // Only a request that named a build has anything to release. A
    // committed-file app, or one released some other way, goes straight
    // to `provision`.
    let published = null;
    const buildId = request?.proposed?.build_id || "";
    if (buildId) {
      published = await publishBuild(request.app_type, buildId);
    }
    const installed = await provision(request.app_type, displayName, request.request_id);
    return { installed, published };
  }

  function closeApprove() {
    setApproving(null);
    setStage(null);
    setRefusal(null);
  }

  async function decide(kind, path, body, request, displayName) {
    setBusy(true);
    setRefusal(null);
    const approvingManifest = kind === "manifest" && path === "approve";
    // The name the app will live under goes with the approval (LO-202), so the
    // platform can release and install it however the approval lands.
    const install = approvingManifest ? installFor(displayName) : null;
    try {
      const result = await post(
        `${ENDPOINT[kind]}/${request.request_id}/${path}`,
        install ? { ...body, install } : body,
      );

      // 202: the approval struck a line while the gate is enforced, so the
      // decision was recorded and nothing was written. There is no `provenance`
      // to read, nothing to release and nothing to install, and the dialog stays
      // open on the wait (LO-191). The queue is re-read so the card says a
      // decision is recorded.
      if (approvingManifest && isWaiting(result)) {
        setStage({
          phase: "waiting",
          requestId: request.request_id,
          decisionId: result.decision?.decision_id || "",
          accepted: result,
          displayName,
          immediate: false,
        });
        await load();
        return;
      }

      setApproving(null);
      setRejecting(null);

      // The approval is written. Anything below is a follow-on and cannot
      // undo it.
      let installed = null;
      let published = null;
      // The kernel recorded the name: the platform releases and installs, and this
      // window does not (LO-202). A kernel that recorded none is followed here.
      const following = approvingManifest ? startedFollowThrough(result?.request) : null;
      if (approvingManifest && windowFollowsOn(result)) {
        ({ installed, published } = await followOn(request, displayName));
      }

      // Re-read rather than patched in place. A screen that updated itself
      // from what it asked for would be showing what it wanted rather than
      // what the kernel now holds.
      await load();
      setOutcome({ ok: true, kind, body: result, installed, published, following });
    } catch (e) {
      // The gate's own four refusals are about the sandbox and are acted on in
      // the dialog, where the approver can wait and press the button again.
      // Closing the dialog on them would put the sentence on a page they have
      // to scroll back to and take the lines they just chose away.
      const gate = approvingManifest ? gateRefusal(e) : null;
      if (gate) {
        setRefusal(gate);
        return;
      }
      setOutcome({ ok: false, kind, error: e });
      closeApprove();
      setRejecting(null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * The request a waiting dialog was reading is now approved, and the read that
   * said so is the one that wrote it. What an approval is followed by is done
   * here, once, and the dialog switches to saying what happened.
   */
  async function land(request, view, progress) {
    setBusy(true);
    try {
      // With the name recorded on the decision the platform follows it through
      // (LO-202); this window only does it for a decision that recorded none.
      const following = startedFollowThrough(view);
      const { installed, published } = following
        ? { installed: null, published: null }
        : await followOn(request, stage?.displayName);
      await load();
      const approvedVersion = progress.version || view?.approved?.version || 0;
      const ceiling = view?.approved || {};
      const outcome = {
        ok: true,
        kind: "manifest",
        landed: { progress, view },
        following,
        body: {
          state: "approved",
          app_type: view?.app_type || request.app_type,
          version: approvedVersion,
          grants_nothing: !(ceiling.reads?.length || ceiling.service_reads?.length
            || ceiling.actions?.length || ceiling.service_actions?.length),
          does_not_prove: stage?.accepted?.does_not_prove || [],
        },
        installed,
        published,
      };
      setOutcome(outcome);
      setStage({ phase: "landed", outcome });
    } finally {
      setBusy(false);
    }
  }

  /**
   * The release and the install an approval left undone (LO-196).
   *
   * The approval is already written and is not touched. This is `followOn`, the same
   * two calls an approval is followed by when its window is open, with the name typed
   * in `FinishDialog`. Safe to repeat: a build that is already released answers
   * `already_published`, and an app that is already installed is reported as that and
   * not installed again.
   */
  async function finish(request, displayName) {
    setBusy(true);
    try {
      const { installed, published } = await followOn(request, displayName);
      setFinishing(null);
      await load();
      setOutcome({
        ok: true,
        kind: "manifest",
        finished: { decidedBy: request.decided_by },
        body: {
          state: "approved",
          app_type: request.app_type,
          version: Number(request.approved?.version || 0),
          does_not_prove: [],
        },
        installed,
        published,
      });
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <Loading label="Reading the queue" />;

  // Whichever kind loaded first — the governor list and the person deciding
  // are the same across all three, so this is one fact and not three that
  // could disagree. Only when all three failed is there nothing to read it
  // from at all, and that is a page-level refusal rather than three empty
  // sections each saying so separately.
  const governance = data.manifest?.governance
    || data.object_type?.governance
    || data.syncmap?.governance
    || data.map_override?.governance
    || null;

  if (!governance) {
    const anyFailure = kindFailure.manifest || kindFailure.object_type
      || kindFailure.syncmap || kindFailure.map_override;
    return <Refusal failure={anyFailure} actor="This console" onRetry={load} />;
  }

  const whyNotByKind = {
    manifest: buildWhyNot(<code>manifest.review</code>, governance),
    object_type: buildWhyNot(<code>objecttype.approve</code>, governance),
    syncmap: buildWhyNot(<code>connector.map.approve</code>, governance),
    map_override: buildWhyNot(
      <code>connector.map_override.approve</code>, governance),
  };

  const merged = [];
  for (const kind of KINDS) {
    const body = data[kind];
    if (!body) continue;
    for (const r of body.requests) merged.push({ kind, r });
  }
  merged.sort((a, b) => (a.r.requested_at < b.r.requested_at ? 1 : -1));

  const pending = merged.filter((x) => x.r.state === "pending");
  const decided = merged.filter((x) => x.r.state !== "pending");
  const approvedCount = decided.filter((x) => x.r.state === "approved").length;
  const rejectedCount = decided.filter((x) => x.r.state === "rejected").length;
  const decidedShown = decided.filter(
    (x) => decidedFilter === "all" || x.r.state === decidedFilter,
  );

  function sandboxOf(request) {
    const control = sandboxControl[request.request_id];
    // Not answered yet: say nothing for the length of a round trip, rather
    // than "state not readable" and then the real answer a moment later.
    if (control === undefined && token && wantsSandbox(request)) {
      return null;
    }
    return sandboxState(request, control);
  }

  // The lines the sandbox did not use, for the approve dialog to flag beside
  // the boxes that strike them. Nothing is flagged from a log that is empty.
  function narrowOf(request) {
    const read = request ? evidence[request.request_id] : null;
    return read ? narrowing(read.calls?.body, read.egress?.body) : NO_NARROWING;
  }

  function bodyFor(kind, request) {
    if (kind === "manifest") {
      return (
        <ManifestBody request={request} current={currentByApp[request.app_type]}
          sandbox={sandboxOf(request)}
          evidence={evidence[request.request_id]} get={get} token={token}
          reload={reload} />
      );
    }
    const Body = NON_MANIFEST_BODY[kind];
    return Body ? <Body request={request} /> : null;
  }

  function renderCard({ kind, r }) {
    return (
      <RequestCard
        key={`${kind}:${r.request_id}`}
        request={r}
        kind={kind}
        title={titleFor(kind, r)}
        extraChips={kind === "manifest" ? (
          <ManifestChips request={r} sandbox={sandboxOf(r)} />
        ) : null}
        notice={kind === "manifest" ? (
          <>
            <FollowThroughNotice
              request={r}
              check={followCheck[r.request_id]}
              busy={busy}
              onFinish={(req) => setFinishing(req)}
            />
            <FollowingNotice
              request={r}
              record={followRecord[r.request_id]}
              busy={busy}
              onFinish={(req) => setFinishing(req)}
            />
          </>
        ) : null}
        busy={busy}
        mayDecide={governance.may_govern}
        whyNot={whyNotByKind[kind]}
        onApprove={(req) => {
          setRefusal(null);
          setStage(null);
          setApproving({ kind, request: req });
        }}
        onFollow={(req) => {
          // The way back to a decision that is recorded and waiting: the dialog
          // opens on the wait, and its first read is at once.
          setRefusal(null);
          setStage({
            phase: "waiting",
            requestId: req.request_id,
            decisionId: req.approval_pending?.decision_id || "",
            accepted: null,
            view: req,
            displayName: "",
            immediate: true,
          });
          setApproving({ kind, request: req });
        }}
        onReject={(req) => setRejecting({ kind, request: req })}
      >
        {bodyFor(kind, r)}
      </RequestCard>
    );
  }

  return (
    <Stack spacing={2} sx={{ minWidth: 0, ...WRAPS }}>
      {/* Two of these were full-width info alerts above the queue, which
          meant a reviewer opening this screen met a briefing before they met
          the thing they came to decide. Both are true and neither is news
          after the first time — so they are one line and a control, and the
          queue is what the page opens on.

          The third one stays an alert, because it is not a briefing: it is
          this reader's own standing, right now, and it changes. */}
      {data.manifest ? (
        <Stack spacing={0.75}>
          <Disclose
            summary="A request grants nothing — approving is the write."
            label="What that means"
          >
            <Typography variant="body2" color="text.secondary">
              An app writes down what it would like to be allowed; no read
              consults that row. Approving writes the manifest, and from the next
              request an app pointed at that app type reads what you granted. You
              can strike lines while approving; you cannot add any. Object types,
              sync mappings and mapping overrides, below, work the same way —
              proposed, then either approved exactly as asked or rejected with a
              reason.
            </Typography>
          </Disclose>
          <Disclose
            summary="Tags restrict. They do not grant."
            label="What that means"
          >
            <Typography variant="body2" color="text.secondary">
              {data.manifest.tags_restrict}
            </Typography>
          </Disclose>
        </Stack>
      ) : null}

      {KINDS.map((kind) => (
        <KindNotice
          key={kind}
          label={KIND_META[kind].label}
          failure={kindFailure[kind]}
          onRetry={load}
        />
      ))}

      {governance.may_govern ? (
        <Alert severity="success" variant="outlined">
          <AlertTitle>You may decide these</AlertTitle>
          Two gates pass on every kind below: this console declares the action
          a decision needs, and you are on this kernel&rsquo;s governor list.
          Being a governor gates these decisions and nothing else — it grants
          you no tag and shows you no record you could not already see.
        </Alert>
      ) : (
        <Alert
          severity={governance.configured ? "info" : "error"}
          variant="outlined"
        >
          <AlertTitle>
            {governance.configured
              ? "You may read this queue and not decide it"
              : "Nobody can decide these yet"}
          </AlertTitle>
          {buildWhyNot("the action each card below names", governance)}
        </Alert>
      )}

      {outcome ? (
        <OutcomeAlert outcome={outcome} onClose={() => setOutcome(null)} />
      ) : null}

      <Box>
        <Label>Waiting on you ({pending.length})</Label>
        {pending.length ? (
          <Stack spacing={1.5}>{pending.map(renderCard)}</Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing pending. An app proposes an app manifest, an object type, a
            sync mapping or a mapping override by calling{" "}
            <code>POST /manifest-requests</code>, <code>POST
            /object-type-requests</code>, <code>POST
            /connector-map-requests</code> or <code>POST
            /map-override-requests</code> respectively; this console decides
            them and does not make them.
          </Typography>
        )}
      </Box>

      {decided.length ? (
        <>
          <Divider />
          <Box>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "baseline", flexWrap: "wrap", mb: 1 }}
            >
              <Label>Already decided ({decided.length})</Label>
              {/* A filter over history, never over the queue above: approve
                  and reject are never hidden by this control, only which of
                  the two you are looking at once they are done. */}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={decidedFilter}
                onChange={(e, next) => next && setDecidedFilter(next)}
                aria-label="filter decided requests by outcome"
              >
                <ToggleButton value="all" aria-label="all decided requests">
                  All ({decided.length})
                </ToggleButton>
                <ToggleButton
                  value="approved"
                  aria-label="approved requests"
                  sx={{ color: "kernel.permitted" }}
                >
                  Approved ({approvedCount})
                </ToggleButton>
                <ToggleButton
                  value="rejected"
                  aria-label="rejected requests"
                  sx={{ color: "kernel.refused" }}
                >
                  Rejected ({rejectedCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Kept on purpose. A queue that forgets its rejections is handed
              the same request next week with no way to know it is the same
              one.
            </Typography>
            {decidedShown.length ? (
              <Stack spacing={1.5}>{decidedShown.map(renderCard)}</Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No {decidedFilter} requests. Switch the filter above to see
                the rest of the history.
              </Typography>
            )}
          </Box>
        </>
      ) : null}

      <Divider />
      <OpenSandboxes get={get} post={post} onChanged={load} />

      <ApproveDialog
        request={approving?.kind === "manifest" ? approving.request : null}
        narrow={approving?.kind === "manifest" ? narrowOf(approving.request) : NO_NARROWING}
        open={approving?.kind === "manifest"}
        busy={busy}
        refusal={refusal}
        stage={stage}
        landed={stage?.phase === "landed" ? <OutcomeAlert outcome={stage.outcome} /> : null}
        get={get}
        current={approving?.kind === "manifest" ? currentByApp[approving.request.app_type] : null}
        token={token}
        onCancel={closeApprove}
        onStage={setStage}
        onLanded={(view, progress) => land(approving.request, view, progress)}
        onStopped={() => load()}
        onReject={() => {
          const request = approving?.request;
          closeApprove();
          if (request) setRejecting({ kind: "manifest", request });
        }}
        onConfirm={(trimmed, displayName) =>
          decide("manifest", "approve", trimmed, approving.request, displayName)
        }
      />
      <FinishDialog
        request={finishing}
        open={Boolean(finishing)}
        busy={busy}
        onCancel={() => setFinishing(null)}
        onConfirm={(name) => finish(finishing, name)}
      />
      <SimpleApproveDialog
        kind={approving?.kind}
        request={approving && approving.kind !== "manifest" ? approving.request : null}
        open={Boolean(approving) && approving.kind !== "manifest"}
        busy={busy}
        onCancel={() => setApproving(null)}
        onConfirm={() => decide(approving.kind, "approve", {}, approving.request)}
      />
      <RejectDialog
        request={rejecting?.request}
        title={rejecting ? titleFor(rejecting.kind, rejecting.request) : null}
        open={Boolean(rejecting)}
        busy={busy}
        onCancel={() => setRejecting(null)}
        onConfirm={(reason) =>
          decide(rejecting.kind, "reject", { reason }, rejecting.request)
        }
      />
    </Stack>
  );
}
